import React, { useEffect, useMemo, useRef, useState } from "react";
import { apiFetch, apiJson } from "../../auth/authClient";

const API = "/api/udrzba";

function getBonusRecommendedItems(detail) {
  const terminy = Array.isArray(detail?.terminyPraci) ? detail.terminyPraci : [];
  const bonus = terminy.find((x) => String(x?.bonusTyp || "").trim() === "jmeninova_bonusova");
  const list = Array.isArray(bonus?.navrzenePolozky)
    ? bonus.navrzenePolozky.map((x) => String(x || "").trim()).filter(Boolean)
    : [];
  return list;
}

function buildBonusRecommendedText(detail) {
  const items = getBonusRecommendedItems(detail);
  if (!items.length) return "";
  return `Doporučené položky z bonusové návštěvy: ${items.join(", ")}`;
}

function normalizeBonusText(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function findCenikItemForBonus(cenikItems, bonusName) {
  const wanted = normalizeBonusText(bonusName);
  if (!wanted) return null;

  const items = Array.isArray(cenikItems) ? cenikItems : [];

  const exact = items.find((it) => normalizeBonusText(it?.nazev) === wanted);
  if (exact) return exact;

  const partial = items.find((it) => {
    const n = normalizeBonusText(it?.nazev);
    return n.includes(wanted) || wanted.includes(n);
  });
  if (partial) return partial;

  return null;
}

function getMainServiceLabel(detail) {
  const mainId = String(detail?.hlavniSluzbaId || "").trim();
  const mainNameRaw = String(detail?.hlavniSluzba || "").trim();
  const items = Array.isArray(detail?.items) ? detail.items : [];

  if (mainId) {
    const mainItem = items.find((x) => String(x?.cenikId || "").trim() === mainId);
    if (String(mainItem?.nazev || "").trim()) return String(mainItem.nazev).trim();
  }

  if (mainId === "public-balicek-4-plus-1" || mainNameRaw === "balicek_4_plus_1") {
    return "Základní péče o hrob – 4 návštěvy + 5. zdarma";
  }

  const firstNamedItem = items.find((x) => String(x?.nazev || "").trim());
  if (String(firstNamedItem?.nazev || "").trim()) return String(firstNamedItem.nazev).trim();

  if (mainNameRaw) return mainNameRaw;

  if (String(detail?.zadaniPrace || "").trim()) return String(detail.zadaniPrace).trim();

  const sluzby = Array.isArray(detail?.sluzby)
    ? detail.sluzby.map((x) => String(x || "").trim()).filter(Boolean)
    : [];
  if (sluzby.length) return sluzby.join(", ");

  return "";
}

function mapBonusRecommendedItemsToFormItems(detail, cenikItems) {
  const items = getBonusRecommendedItems(detail);

  return items.map((name, index) => {
    const matched = findCenikItemForBonus(cenikItems, name);

    const cenaBezDph = Math.round(Number(
      matched?.cenaBezDph ??
      matched?.cena ??
      matched?.bezDph ??
      0
    ) * 100) / 100;

    const dph = Number(
      matched?.dph ??
      matched?.sazbaDph ??
      21
    );

    const mnozstvi = 1;
    const radekBezDph = Math.round(cenaBezDph * mnozstvi * 100) / 100;
    const radekDph = Math.round(radekBezDph * (dph / 100) * 100) / 100;
    const radekSDph = Math.round((radekBezDph + radekDph) * 100) / 100;

    return {
      id: `bonus-item-${String(name || "").toLowerCase().replace(/[^a-z0-9]+/g, "-")}-${index + 1}`,
      cenikId: matched?.id || "",
      nazev: String(name || "").trim(),
      mnozstvi,
      jednotka: matched?.jednotka || "ks",
      cena: cenaBezDph,
      cenaBezDph,
      dph,
      sazbaDph: dph,
      cenaCelkem: radekSDph,
      radekBezDph,
      radekDph,
      radekSDph,
      priKazdeNavsteve: false,
      zdroj: "bonusova_navsteva",
      bonusTyp: "jmeninova_bonusova",
      poznamka: matched
        ? "Automaticky převzato z bonusové návštěvy"
        : "Automaticky převzato z bonusové návštěvy – bez napojení na ceník",
    };
  }).filter((x) => x.nazev);
}

function formatTerminLabel(item, index) {
  const poradi = Number(item?.poradi || index + 1);
  if (String(item?.nazevNavstevy || "").trim()) return String(item.nazevNavstevy).trim();
  return `Návštěva ${poradi}`;
}

function getVisitAddonItem(detail, kind) {
  const items = Array.isArray(detail?.items) ? detail.items : [];
  const wanted = String(kind || "").trim();

  return items.find((it) => {
    const cenikId = String(it?.cenikId || "").trim();
    if (wanted === "kvetiny") return cenikId === "__balicek_kvetiny__";
    if (wanted === "dekorace") return cenikId === "__balicek_dekorace__";
    return false;
  }) || null;
}

function getVisitAddonInfo(detail, item, kind) {
  const addonItem = getVisitAddonItem(detail, kind);
  const datum = String(item?.datum || "").slice(0, 10);
  if (!addonItem || !datum) return null;

  const vybraneNavstevy = Array.isArray(addonItem?.vybraneNavstevy)
    ? addonItem.vybraneNavstevy.map((x) => String(x || "").slice(0, 10)).filter(Boolean)
    : [];

  const jeVybrano = vybraneNavstevy.includes(datum);
  if (!jeVybrano) return null;

  const castka = Math.round(Number(addonItem?.cenaBezDph ?? addonItem?.cena ?? 0) * 100) / 100;
  return {
    jeVybrano: true,
    castka,
  };
}

function formatTerminDetail(detail, item, index) {
  const label = formatTerminLabel(item, index);
  const datum = String(item?.datum || "").trim() || "-";
  const doplnky = [];

  const kvetinyInfo = getVisitAddonInfo(detail, item, "kvetiny");
  const dekoraceInfo = getVisitAddonInfo(detail, item, "dekorace");

  if (kvetinyInfo?.jeVybrano) doplnky.push(`květiny ${kvetinyInfo.castka} Kč`);
  if (dekoraceInfo?.jeVybrano) doplnky.push(`dekorace ${dekoraceInfo.castka} Kč`);

  const rezim = String(item?.rezimNavstevy || "").trim() === "lehka_udrzba" ? " [lehká údržba]" : "";
  const navrzeneList = Array.isArray(item?.navrzenePolozky)
    ? item.navrzenePolozky.map((v) => String(v || "").trim()).filter(Boolean)
    : [];
  const navrzene = navrzeneList.length
    ? ` {doporučeno: ${navrzeneList.join(", ")}}`
    : "";

  return `${label}: ${datum}${item?.jeZdarma ? " (zdarma)" : ""}${rezim}${navrzene}${doplnky.length ? " [" + doplnky.join(", ") + "]" : ""}`;
}

function periodicitaLabel(v) {
  const x = String(v || "").trim().toLowerCase();
  if (x === "mesicne") return "Měsíčně";
  if (x === "ctvrtletne") return "Čtvrtletně";
  if (x === "pulrocne") return "Půlročně";
  if (x === "rocne") return "Ročně";
  if (x === "dle_domluvy") return "Dle domluvy";
  return v || "-";
}

function plusPeriodLocal(dateStr, periodicita) {
  const base = String(dateStr || "").trim();
  const per = String(periodicita || "").trim().toLowerCase();

  const m = base.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return "";

  const year = Number(m[1]);
  const month = Number(m[2]) - 1;
  const day = Number(m[3]);

  const d = new Date(year, month, day);
  if (Number.isNaN(d.getTime())) return "";

  if (per === "mesicne") d.setMonth(d.getMonth() + 1);
  else if (per === "ctvrtletne") d.setMonth(d.getMonth() + 3);
  else if (per === "pulrocne") d.setMonth(d.getMonth() + 6);
  else if (per === "rocne") d.setFullYear(d.getFullYear() + 1);
  else return "";

  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function plusPeriodForRenewal(dateStr, periodicita) {
  const base = String(dateStr || "").trim();
  const per = String(periodicita || "").trim();

  const m = base.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return "";

  const year = Number(m[1]);
  const month = Number(m[2]) - 1;
  const day = Number(m[3]);

  const d = new Date(year, month, day);
  if (Number.isNaN(d.getTime())) return "";

  if (per === "mesicne") d.setMonth(d.getMonth() + 1);
  else if (per === "ctvrtletne") d.setMonth(d.getMonth() + 3);
  else if (per === "pulrocne") d.setMonth(d.getMonth() + 6);
  else if (per === "rocne") d.setFullYear(d.getFullYear() + 1);
  else return "";

  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function money(v) {
  return `${Number(v || 0).toLocaleString("cs-CZ")} Kč`;
}

function computeBalicekSoucty(items, hlavniSluzbaId, isBalicek, visitMultiplier, includedBalicekVisits) {
  const safeItems = Array.isArray(items) ? items : [];
  const mainItem = safeItems.find(
    (x) => String(x?.cenikId || "") === String(hlavniSluzbaId || "")
  ) || null;

  let bezDph = 0;
  let dphCastka = 0;

  for (const item of safeItems) {
    if (!item) continue;

    const mnozstvi = Number(item.mnozstvi || 0);
    const cenaBezDph = Number(item.cenaBezDph || 0);
    const dph = Number(item.dph || 0);
    const isMain = String(item.cenikId || "") === String(hlavniSluzbaId || "");

    if (isMain && isBalicek) {
      const radekBez = mnozstvi * cenaBezDph;
      bezDph += radekBez;
      dphCastka += radekBez * (dph / 100);
      continue;
    }

    const multiplier =
      isMain
        ? visitMultiplier
        : (item.priKazdeNavsteve ? visitMultiplier : 1);

    const radekBez = mnozstvi * cenaBezDph * multiplier;
    bezDph += radekBez;
    dphCastka += radekBez * (dph / 100);
  }

  if (isBalicek && mainItem) {
    const base = Number(mainItem.cenaBezDph || 0);
    const dph = Number(mainItem.dph || 0);
    const balicekVisitCount = Math.max(0, Number(visitMultiplier || 0));
    const balicekExtraVisits = Math.max(0, balicekVisitCount - includedBalicekVisits);
    const extraUnitBezDph = base / includedBalicekVisits;
    const extraBez = balicekExtraVisits * extraUnitBezDph;

    bezDph += extraBez;
    dphCastka += extraBez * (dph / 100);
  }

  const sDph = bezDph + dphCastka;

  return {
    bezDph: Math.round(bezDph * 100) / 100,
    dph: Math.round(dphCastka * 100) / 100,
    sDph: Math.round(sDph * 100) / 100,
  };
}

function typPeceLabel(v) {
  const x = String(v || "").trim().toLowerCase();
  if (x === "jednorazova") return "Jednorázová";
  if (x === "predplatne") return "Předplatné";
  if (x === "balicek" || x === "balicek_4x") return "Balíček";
  return v || "-";
}

const SUBSCRIPTION_STATUS_LABELS = {
  aktivni: "Aktivní",
  konci_brzy: "Končí brzy",
  ceka_na_obnovu: "Čeká na obnovu",
  ceka_na_realizaci: "Čeká na realizaci",
  vyprselo: "Vypršelo",
};

const RENEWAL_STATUS_LABELS = {
  pripravena: "Připravena",
  potvrzena: "Potvrzena",
};

const VISIT_AFTER_STATUS_LABELS = {
  aktivni: "Aktivní",
  konci_brzy: "Končí brzy",
  ceka_na_obnovu: "Čeká na obnovu",
  ceka_na_realizaci: "Čeká na realizaci",
  vyprselo: "Vypršelo",
  obnoveno: "Obnoveno",
  neobnoveno: "Neobnoveno",
};

function subscriptionStatusLabel(status) {
  const x = String(status || "").trim().toLowerCase();
  return SUBSCRIPTION_STATUS_LABELS[x] || status || "-";
}

function baseBadgeStyle(colors = {}) {
  return {
    display: "inline-flex",
    alignItems: "center",
    padding: "4px 10px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 700,
    whiteSpace: "nowrap",
    border: colors.border || "1px solid #cbd5e1",
    background: colors.background || "#f8fafc",
    color: colors.color || "#334155",
  };
}

function getStatusBadgeStyle(kind, value) {
  const x = String(value || "").trim().toLowerCase();

  const base = baseBadgeStyle();

  if (kind === "predplatne") {
    if (x === "aktivni") {
      return baseBadgeStyle({ background: "#dcfce7", color: "#166534", border: "1px solid #86efac" });
    }
    if (x === "konci_brzy") {
      return baseBadgeStyle({ background: "#fef3c7", color: "#92400e", border: "1px solid #fcd34d" });
    }
    if (x === "ceka_na_obnovu" || x === "vyprselo") {
      return baseBadgeStyle({ background: "#fee2e2", color: "#991b1b", border: "1px solid #fca5a5" });
    }
    if (x === "ceka_na_realizaci") {
      return baseBadgeStyle({ background: "#dbeafe", color: "#1d4ed8", border: "1px solid #93c5fd" });
    }
    return base;
  }

  if (kind === "stav") {
    if (x === "nova") {
      return baseBadgeStyle({ background: "#ede9fe", color: "#6d28d9", border: "1px solid #c4b5fd" });
    }
    if (x === "ceka_na_realizaci") {
      return baseBadgeStyle({ background: "#dbeafe", color: "#1d4ed8", border: "1px solid #93c5fd" });
    }
    if (x === "hotovo") {
      return baseBadgeStyle({ background: "#dcfce7", color: "#166534", border: "1px solid #86efac" });
    }
    if (x === "vyfakturovano") {
      return baseBadgeStyle({ background: "#cffafe", color: "#155e75", border: "1px solid #67e8f9" });
    }
    if (x === "zruseno") {
      return baseBadgeStyle({ background: "#f3f4f6", color: "#374151", border: "1px solid #d1d5db" });
    }
    return base;
  }

  if (kind === "platba") {
    if (x === "uhrazeno") {
      return baseBadgeStyle({ background: "#dcfce7", color: "#166534", border: "1px solid #86efac" });
    }
    if (x === "castecne") {
      return baseBadgeStyle({ background: "#fef3c7", color: "#92400e", border: "1px solid #fcd34d" });
    }
    if (x === "neuhrazeno") {
      return baseBadgeStyle({ background: "#fee2e2", color: "#991b1b", border: "1px solid #fca5a5" });
    }
    return base;
  }

  return base;
}

function renewalStatusLabel(status) {
  const x = String(status || "").trim().toLowerCase();
  return RENEWAL_STATUS_LABELS[x] || status || "-";
}

function renewalStatusBadgeStyle(status) {
  const x = String(status || "").trim().toLowerCase();

  const base = {
    display: "inline-flex",
    alignItems: "center",
    padding: "4px 10px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 700,
    whiteSpace: "nowrap",
    border: "1px solid #cbd5e1",
    background: "#f8fafc",
    color: "#334155",
  };

  if (x === "pripravena") {
    return baseBadgeStyle({ background: "#fef3c7", color: "#92400e", border: "1px solid #fcd34d" });
  }

  if (x === "potvrzena") {
    return baseBadgeStyle({ background: "#dcfce7", color: "#166534", border: "1px solid #86efac" });
  }

  return base;
}

function visitAfterStatusLabel(status) {
  const x = String(status || "").trim().toLowerCase();
  return VISIT_AFTER_STATUS_LABELS[x] || status || "-";
}

function visitAfterStatusBadgeStyle(status) {
  const x = String(status || "").trim().toLowerCase();

  if (x === "aktivni") {
    return {
      display: "inline-flex",
      alignItems: "center",
      padding: "4px 10px",
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 700,
      background: "#dcfce7",
      color: "#166534",
      border: "1px solid #86efac",
      whiteSpace: "nowrap",
    };
  }

  if (x === "konci_brzy") {
    return {
      display: "inline-flex",
      alignItems: "center",
      padding: "4px 10px",
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 700,
      background: "#fef3c7",
      color: "#92400e",
      border: "1px solid #fcd34d",
      whiteSpace: "nowrap",
    };
  }

  if (x === "ceka_na_obnovu" || x === "vyprselo") {
    return {
      display: "inline-flex",
      alignItems: "center",
      padding: "4px 10px",
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 700,
      background: "#fee2e2",
      color: "#991b1b",
      border: "1px solid #fca5a5",
      whiteSpace: "nowrap",
    };
  }

  if (x === "ceka_na_realizaci") {
    return {
      display: "inline-flex",
      alignItems: "center",
      padding: "4px 10px",
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 700,
      background: "#dbeafe",
      color: "#1d4ed8",
      border: "1px solid #93c5fd",
      whiteSpace: "nowrap",
    };
  }

  if (x === "obnoveno") {
    return {
      display: "inline-flex",
      alignItems: "center",
      padding: "4px 10px",
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 700,
      background: "#e0f2fe",
      color: "#075985",
      border: "1px solid #7dd3fc",
      whiteSpace: "nowrap",
    };
  }

  if (x === "neobnoveno") {
    return {
      display: "inline-flex",
      alignItems: "center",
      padding: "4px 10px",
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 700,
      background: "#f3f4f6",
      color: "#374151",
      border: "1px solid #d1d5db",
      whiteSpace: "nowrap",
    };
  }

  return {
    display: "inline-flex",
    alignItems: "center",
    padding: "4px 10px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 700,
    background: "#f8fafc",
    color: "#334155",
    border: "1px solid #cbd5e1",
    whiteSpace: "nowrap",
  };
}

function visitAfterStatusRowStyle(status) {
  const x = String(status || "").trim().toLowerCase();

  if (x === "aktivni") return { background: "#f0fdf4" };
  if (x === "konci_brzy") return { background: "#fffbeb" };
  if (x === "ceka_na_obnovu" || x === "vyprselo") return { background: "#fef2f2" };
  if (x === "ceka_na_realizaci") return { background: "#eff6ff" };

  return {};
}

function historyStateBadgeStyle(status) {
  const x = String(status || "").trim().toLowerCase();

  if (x === "nova") return baseBadgeStyle({ background: "#ede9fe", color: "#6d28d9", border: "1px solid #c4b5fd" });
  if (x === "naplanovano") return baseBadgeStyle({ background: "#eff6ff", color: "#1d4ed8", border: "1px solid #93c5fd" });
  if (x === "rozpracovano") return baseBadgeStyle({ background: "#fff7ed", color: "#c2410c", border: "1px solid #fdba74" });
  if (x === "dokonceno") return baseBadgeStyle({ background: "#dcfce7", color: "#166534", border: "1px solid #86efac" });
  if (x === "vyfakturovano") return baseBadgeStyle({ background: "#cffafe", color: "#155e75", border: "1px solid #67e8f9" });
  if (x === "zruseno" || x === "zdarma" || x === "interni_test") return baseBadgeStyle({ background: "#f3f4f6", color: "#374151", border: "1px solid #d1d5db" });

  return baseBadgeStyle();
}

function historyStateRowStyle(status) {
  const x = String(status || "").trim().toLowerCase();

  if (x === "nova") return { background: "#faf5ff" };
  if (x === "naplanovano") return { background: "#eff6ff" };
  if (x === "rozpracovano") return { background: "#fff7ed" };
  if (x === "dokonceno") return { background: "#f0fdf4" };
  if (x === "vyfakturovano") return { background: "#ecfeff" };
  if (x === "zruseno" || x === "zdarma" || x === "interni_test") return { background: "#f8fafc" };

  return {};
}

function formatDate(value) {
  const raw = String(value || "").trim();
  if (!raw) return "-";

  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const [y, m, d] = raw.split("-");
    return `${d}. ${m}. ${y}`;
  }

  const dt = new Date(raw);
  if (Number.isNaN(dt.getTime())) return raw;
  return dt.toLocaleDateString("cs-CZ");
}

function formatDateTime(value) {
  const raw = String(value || "").trim();
  if (!raw) return "-";

  const dt = new Date(raw);
  if (Number.isNaN(dt.getTime())) {
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return formatDate(raw);
    return raw;
  }

  return dt.toLocaleString("cs-CZ", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function shiftIsoDate(dateStr, days) {
  const raw = String(dateStr || "").trim();
  if (!raw) return "";
  const m = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return "";
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  if (Number.isNaN(d.getTime())) return "";
  d.setDate(d.getDate() + Number(days || 0));
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(String(fr.result || ""));
    fr.onerror = reject;
    fr.readAsDataURL(file);
  });
}

const inputStyle = {
  width: "100%",
  padding: "11px 12px",
  borderRadius: 12,
  border: "1px solid #dbe3ee",
  fontSize: 14,
  background: "#ffffff",
  color: "#111827",
  outline: "none",
  boxSizing: "border-box",
  boxShadow: "0 1px 2px rgba(15,23,42,0.03)",
};

const secondaryButtonStyle = {
  padding: "10px 14px",
  borderRadius: 12,
  border: "1px solid #dbe3ee",
  background: "#ffffff",
  color: "#0f172a",
  fontSize: 14,
  fontWeight: 600,
  cursor: "pointer",
  boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
};

const primaryButtonStyle = {
  padding: "10px 14px",
  borderRadius: 12,
  border: "1px solid #1d4ed8",
  background: "#2563eb",
  color: "#ffffff",
  fontSize: 14,
  fontWeight: 700,
  cursor: "pointer",
  boxShadow: "0 4px 10px rgba(37,99,235,0.18)",
};

const dangerButtonStyle = {
  padding: "10px 14px",
  borderRadius: 12,
  border: "1px solid #fecaca",
  background: "#fff1f2",
  color: "#b91c1c",
  fontSize: 14,
  fontWeight: 700,
  cursor: "pointer",
};

const disabledButtonStyle = {
  opacity: 0.55,
  cursor: "not-allowed",
};

const checkboxRowStyle = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  paddingTop: 10,
  fontSize: 14,
  color: "#334155",
};

const topDetailGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
  gap: 16,
  marginBottom: 16,
};

const topDetailCardStyle = {
  padding: "16px 18px",
  border: "1px solid #e5e7eb",
  borderRadius: 14,
  background: "#ffffff",
  boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
};

const topDetailMetaGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0,1fr))",
  gap: 8,
};

const topDetailItemStyle = {
  display: "flex",
  flexDirection: "column",
  gap: 4,
  minWidth: 0,
};

const topDetailLabelStyle = {
  fontSize: 12,
  fontWeight: 500,
  color: "#64748b",
  lineHeight: 1.2,
};

const topDetailValueStyle = {
  fontSize: 14,
  fontWeight: 600,
  color: "#111827",
  lineHeight: 1.35,
  wordBreak: "break-word",
};

const topDetailPriorityItemStyle = {
  ...topDetailItemStyle,
  padding: "10px 12px",
  borderRadius: 12,
  background: "#f8fafc",
  border: "1px solid #cbd5e1",
  boxShadow: "0 1px 2px rgba(15, 23, 42, 0.05)",
};

const topDetailPriorityValueStyle = {
  ...topDetailValueStyle,
  fontSize: 18,
  fontWeight: 800,
  color: "#0f172a",
};

function stavLabel(stav) {
  if (stav === "nova") return "Nová";
  if (stav === "naplanovano") return "Naplánováno";
  if (stav === "rozpracovano") return "Rozpracováno";
  if (stav === "dokonceno") return "Dokončeno";
  if (stav === "vyfakturovano") return "Vyfakturováno";
  if (stav === "zruseno") return "Zrušeno";
  if (stav === "zdarma") return "Zdarma";
  if (stav === "interni_test") return "Interní test";
  return "-";
}

function stavBadgeStyle(stav) {
  const map = {
    nova: {
      background: "#eff6ff",
      color: "#1d4ed8",
      border: "1px solid #bfdbfe",
    },
    naplanovano: {
      background: "#f8fafc",
      color: "#334155",
      border: "1px solid #cbd5e1",
    },
    rozpracovano: {
      background: "#fff7ed",
      color: "#c2410c",
      border: "1px solid #fdba74",
    },
    dokonceno: {
      background: "#ecfdf5",
      color: "#047857",
      border: "1px solid #86efac",
    },
    vyfakturovano: {
      background: "#f5f3ff",
      color: "#6d28d9",
      border: "1px solid #c4b5fd",
    },
    zruseno: {
      background: "#f8fafc",
      color: "#64748b",
      border: "1px solid #cbd5e1",
    },
    zdarma: {
      background: "#fefce8",
      color: "#a16207",
      border: "1px solid #fde68a",
    },
    interni_test: {
      background: "#f1f5f9",
      color: "#475569",
      border: "1px solid #cbd5e1",
    },
  };

  const cfg = map[String(stav || "").trim()] || {
    background: "#f8fafc",
    color: "#334155",
    border: "1px solid #cbd5e1",
  };

  return {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 28,
    padding: "4px 10px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.01em",
    whiteSpace: "nowrap",
    background: cfg.background,
    color: cfg.color,
    border: cfg.border,
    boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
  };
}

function financniStavLabel(v) {
  const x = String(v || "").trim().toLowerCase();
  if (x === "zaplaceno") return "Zaplaceno";
  if (x === "zalohovano") return "Zálohováno";
  if (x === "nezaplaceno") return "Nezaplaceno";
  return v || "-";
}

function financniStavBadgeStyle(v) {
  const map = {
    nezaplaceno: {
      background: "#fff1f2",
      color: "#be123c",
      border: "1px solid #fecdd3",
    },
    zalohovano: {
      background: "#fff7ed",
      color: "#c2410c",
      border: "1px solid #fdba74",
    },
    zaplaceno: {
      background: "#ecfdf5",
      color: "#047857",
      border: "1px solid #86efac",
    },
  };

  const cfg = map[String(v || "").trim().toLowerCase()] || {
    background: "#f8fafc",
    color: "#334155",
    border: "1px solid #cbd5e1",
  };

  return {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 28,
    padding: "4px 10px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.01em",
    whiteSpace: "nowrap",
    background: cfg.background,
    color: cfg.color,
    border: cfg.border,
    boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
  };
}

function Gallery({ title, items, kind, onPick, inputRef, uploading, onRemove }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 10,
          marginBottom: 8,
          flexWrap: "wrap",
        }}
      >
        <div style={{ fontWeight: 700 }}>{title}</div>
        <div>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={onPick}
          />
        </div>
      </div>

      {uploading ? <div style={{ marginBottom: 8 }}>Nahrávám fotky…</div> : null}

      {(items || []).length ? (
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {items.map((url, i) => (
            <div
              key={i}
              style={{
                border: "1px solid #e5e7eb",
                borderRadius: 10,
                padding: 8,
                background: "#fff",
              }}
            >
              <a href={url} target="_blank" rel="noreferrer">
                <img
                  src={url}
                  alt=""
                  style={{
                    width: 120,
                    height: 120,
                    objectFit: "cover",
                    borderRadius: 8,
                    display: "block",
                  }}
                />
              </a>
              <button
                type="button"
                onClick={() => onRemove(url)}
                style={{ marginTop: 8, width: "100%" }}
              >
                Smazat fotku
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ color: "#6b7280" }}>
          Zatím bez fotek {kind === "fotoPo" ? "po" : "před"}.
        </div>
      )}
    </div>
  );
}


export default function UdrzbaDetailZakazky({ item, onClose, onSaved }) {

  const sectionCardStyle = {
    background: "#ffffff",
    border: "1px solid #e5e7eb",
    borderRadius: 14,
    padding: "16px 18px",
    boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
    marginBottom: 16,
  };

  const sectionTitleStyle = {
    fontWeight: 700,
    fontSize: 15,
    marginBottom: 10,
    color: "#0f172a",
    lineHeight: 1.25,
  };

  const softBlockStyle = {
    background: "#f8fafc",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    padding: 12,
  };

  const summaryMiniCardStyle = {
    padding: "12px 14px",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    background: "#ffffff",
    boxShadow: "0 1px 2px rgba(15,23,42,0.03)",
  };

  const tableWrapStyle = {
    overflowX: "auto",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    background: "#ffffff",
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.6)",
  };

  const thStyle = {
    textAlign: "left",
    padding: "10px 12px",
    borderBottom: "1px solid #e5e7eb",
    color: "#64748b",
    fontSize: 12,
    fontWeight: 600,
    background: "#f8fafc",
    whiteSpace: "nowrap",
  };

  const tdStyle = {
    padding: "10px 12px",
    borderBottom: "1px solid #f1f5f9",
    verticalAlign: "top",
    color: "#111827",
  };

  const chipBaseStyle = {
    padding: "6px 10px",
    borderRadius: 999,
    fontSize: 13,
    border: "1px solid #e5e7eb",
    background: "#f8fafc",
  };

  const primaryButtonStyle = {
    padding: "10px 14px",
    borderRadius: 10,
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    cursor: "pointer",
    fontWeight: 600,
  };
  const [detail, setDetail] = useState(item);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const [cenik, setCenik] = useState([]);
  const [selectedCenikId, setSelectedCenikId] = useState("");
  const [customName, setCustomName] = useState("Jiné");
  const [customPrice, setCustomPrice] = useState(0);

  const [uploadingPred, setUploadingPred] = useState(false);
  const [uploadingPo, setUploadingPo] = useState(false);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [renewEmailLoading, setRenewEmailLoading] = useState(false);
  const [renewEmailPreviewLoading, setRenewEmailPreviewLoading] = useState(false);
  const [renewEmailPreview, setRenewEmailPreview] = useState(null);
  const [doneEmailLoading, setDoneEmailLoading] = useState(false);
  const [doneEmailPreviewLoading, setDoneEmailPreviewLoading] = useState(false);
  const [doneEmailPreview, setDoneEmailPreview] = useState(null);
  const [nakupDraft, setNakupDraft] = useState({
    nazev: "",
    dodavatel: "",
    datum: new Date().toISOString().slice(0, 10),
    mnozstvi: 1,
    cenaZaKsSDph: "",
    poznamka: "",
    zahrnoutDoFaktury: true,
    popisDoFaktury: "",
  });

  const predRef = useRef(null);
  const poRef = useRef(null);

  const [form, setForm] = useState(item);
  const [quickMoveDate, setQuickMoveDate] = useState("");
  const careType = String(form?.typPece || detail?.typPece || "").trim();
  const isJednorazova = careType === "jednorazova";
  const isPredplatne = careType === "predplatne";
  const isBalicek = careType === "balicek" || careType === "balicek_4x";

  const includedBalicekVisits = 4;
  const bonusRecommendedItems = getBonusRecommendedItems(detail);

  function appendBonusItemsToPoznamka() {
    const text = buildBonusRecommendedText(detail);
    if (!text) return;

    setForm((prev) => {
      const currentPoznamka = String(prev?.poznamka || "").trim();
      if (currentPoznamka.includes(text)) return prev;

      return {
        ...prev,
        poznamka: currentPoznamka ? `${currentPoznamka}\n${text}` : text,
      };
    });
  }

  function appendBonusItemsToFormItems() {
    const bonusItems = mapBonusRecommendedItemsToFormItems(detail, cenik);
    if (!bonusItems.length) return;

    setForm((prev) => {
      const currentItems = Array.isArray(prev?.items) ? prev.items : [];

      const existingKeys = new Set(
        currentItems.map((x) => `${String(x?.nazev || "").trim()}|${String(x?.zdroj || "").trim()}`)
      );

      const toAdd = bonusItems.filter(
        (x) => !existingKeys.has(`${String(x?.nazev || "").trim()}|${String(x?.zdroj || "").trim()}`)
      );

      if (!toAdd.length) return prev;

      return {
        ...prev,
        items: [...currentItems, ...toAdd],
      };
    });
  }

  const activePlanNavstev = Array.isArray(form?.planNavstev)
    ? form.planNavstev
    : (Array.isArray(detail?.planNavstev) ? detail.planNavstev : []);
  const activeVisitCount = isJednorazova
    ? 1
    : Math.max(
        0,
        activePlanNavstev.length ||
        Number(form?.pocetNavstevCelkem || detail?.pocetNavstevCelkem || 0)
      );
  const activeHlavniSluzbaId = String(form?.hlavniSluzbaId || detail?.hlavniSluzbaId || "").trim();
  const activeItems = Array.isArray(form?.items)
      ? form.items
      : (Array.isArray(detail?.items) ? detail.items : []);
    const doneVisitsCount = Array.isArray(detail?.navstevy) ? detail.navstevy.length : 0;
    const totalVisitsCount = Number(detail?.pocetNavstevCelkem || 0);
    const remainingVisitsCount = Number(
      detail?.zbyvaNavstev ?? Math.max(0, totalVisitsCount - doneVisitsCount)
    );

  const balicekExtraVisits = isBalicek ? Math.max(0, activeVisitCount - includedBalicekVisits) : 0;
  const souctyReal = computeBalicekSoucty(
    activeItems,
    activeHlavniSluzbaId,
    isBalicek,
    activeVisitCount,
    includedBalicekVisits
  );


  useEffect(() => {
      setDetail(item);
      setForm(item);
      setQuickMoveDate(String(item?.planovaneDatum || item?.dalsiNavsteva || "").slice(0, 10));
    }, [item]);


  useEffect(() => {
    if (!successMessage) return;
    const t = setTimeout(() => setSuccessMessage(""), 3200);
    return () => clearTimeout(t);
  }, [successMessage]);

  useEffect(() => {
    apiJson(`${API}/cenik`, { method: "GET" })
      .then((d) => {
        const arr = Array.isArray(d?.items) ? d.items : [];
        setCenik(arr);
        if (arr.length && !selectedCenikId) {
          setSelectedCenikId(arr[0].id);
        }
      })
      .catch(() => setCenik([]));
  }, [selectedCenikId]);

  function calcRow(it) {
  const mnozstvi = Number(it?.mnozstvi || 1);
  const cenaBezDph = Number(it?.cenaBezDph || 0);
  const sazba = Number(it?.dph || 21);

  const bez = Math.round(cenaBezDph * mnozstvi * 100) / 100;
  const dph = Math.round(bez * (sazba / 100) * 100) / 100;
  const sdph = Math.round((bez + dph) * 100) / 100;

  return { bez, dph, sdph };
}

const souctyPreview = useMemo(() => {
    const items = Array.isArray(form.items) ? form.items : [];
    let bez = 0;
    let dph = 0;
    let sdph = 0;

    items.forEach((it) => {
      const row = calcRow(it);
      bez += row.bez;
      dph += row.dph;
      sdph += row.sdph;
    });

    return {
      bezDph: Math.round(bez * 100) / 100,
      dph: Math.round(dph * 100) / 100,
      sDph: Math.round(sdph * 100) / 100,
    };
  }, [form]);

  const cenaCelkemNum = Math.round(Number(souctyReal.sDph || 0) * 100) / 100;
  const zalohaCastkaNum = Math.round(Number(form.zalohaCastka || 0) * 100) / 100;
  const uhrazenoCelkemNum = Math.round(Number(form.uhrazenoCelkem || 0) * 100) / 100;
  const doplatekCastkaNum = Math.max(0, Math.round((cenaCelkemNum - uhrazenoCelkemNum) * 100) / 100);


  function validateDetailForm(form, isJednorazova, isPredplatne, isBalicek, cenaCelkemNum, uhrazenoCelkemNum) {
    const errors = [];

    if (!String(form.objednavatel || "").trim()) {
      errors.push("Vyplň jméno objednavatele.");
    }

    if (!String(form.hrbitov || "").trim()) {
      errors.push("Vyplň hřbitov.");
    }

    const hasPresnaLokace =
      !!String(form.cisloHrobu || "").trim() ||
      !!String(form.oddeleni || "").trim() ||
      !!String(form.skupina || "").trim();

    const hasNahradniIdentifikace =
      !!String(form.majitelHrobuJmeno || "").trim() ||
      !!String(form.zesnulyPrijmeni || "").trim() ||
      !!String(form.zesnulyDatumUmrti || "").trim() ||
      !!String(form.jmenoNaDesce || "").trim();

    if (!hasPresnaLokace && !hasNahradniIdentifikace) {
      errors.push("Vyplň číslo hrobu nebo údaje pro dohledání hrobu.");
    }

    if (!String(form.typPece || "").trim()) {
      errors.push("Vyber typ péče.");
    }

    if (!String(form.planovaneDatum || "").trim()) {
      errors.push("Vyplň nejbližší plánovanou návštěvu.");
    }

    if (!isJednorazova && !String(form.periodicita || "").trim()) {
      errors.push("Vyber periodicitu.");
    }

    if (isPredplatne && !String(form.uhrazenoDo || "").trim()) {
      errors.push("U předplatného vyplň datum uhrazeno do.");
    }

    if (!isJednorazova && !String(form.dalsiNavsteva || "").trim()) {
      errors.push("U opakované péče vyplň datum další návštěvy.");
    }

    if (isBalicek) {
      const celkem = Array.isArray(form.planNavstev)
        ? form.planNavstev.length
        : Number(form.pocetNavstevCelkem || 0);

      if (celkem <= 0) {
        errors.push("U balíčku musí být alespoň jeden skutečný termín návštěvy.");
      }
    }

    if (Number(uhrazenoCelkemNum || 0) < 0) {
      errors.push("Uhrazeno celkem nemůže být záporné.");
    }

    if (Number(cenaCelkemNum || 0) < 0) {
      errors.push("Cena celkem nemůže být záporná.");
    }

    const hasItems = Array.isArray(form.items) && form.items.length > 0;
    const hasZadani = !!String(form.zadaniPrace || "").trim();

    if (!hasItems && !hasZadani) {
      errors.push("Vyber alespoň jednu službu z ceníku nebo vyplň zadání práce.");
    }

    return errors;
  }

  const financniStavAuto =
    uhrazenoCelkemNum <= 0
      ? "nezaplaceno"
      : (cenaCelkemNum > 0 && uhrazenoCelkemNum + 0.001 < cenaCelkemNum)
        ? "zalohovano"
        : "zaplaceno";


  function calcInterniSoucty(nakupy, prodejSDph) {
  const arr = Array.isArray(nakupy) ? nakupy : [];
  const nakupCelkemSDph = Math.round(
    arr.reduce((sum, it) => sum + Number(calcNakupRow(it).celkemSDph || 0), 0) * 100
  ) / 100;

  const kFakturaci = arr.filter((it) => !!it?.zahrnoutDoFaktury);
  const kFakturaciNakupSDph = Math.round(
    kFakturaci.reduce((sum, it) => sum + Number(calcNakupRow(it).celkemSDph || 0), 0) * 100
  ) / 100;

  const cilovaMarzeProc = 50;
  const doporucenaProdejniCenaSDph = Math.round(nakupCelkemSDph * 1.5 * 100) / 100;
  const doporucenaProdejniCenaKfakturaciSDph = Math.round(kFakturaciNakupSDph * 1.5 * 100) / 100;
  const skutecnyProdejSDph = Math.round(Number(prodejSDph || 0) * 100) / 100;
  const skutecnaMarzeSDph = Math.round((skutecnyProdejSDph - nakupCelkemSDph) * 100) / 100;
  const skutecnaMarzeProcZNakupu = nakupCelkemSDph > 0
    ? Math.round((skutecnaMarzeSDph / nakupCelkemSDph) * 10000) / 100
    : 0;

  return {
    cilovaMarzeProc,
    nakupCelkemSDph,
    kFakturaciPocet: kFakturaci.length,
    kFakturaciNakupSDph,
    doporucenaProdejniCenaSDph,
    doporucenaProdejniCenaKfakturaciSDph,
    skutecnyProdejSDph,
    skutecnaMarzeSDph,
    skutecnaMarzeProcZNakupu,
  };
}

function calcNakupRow(nakup) {
  const mnozstvi = Number(nakup?.mnozstvi || 0);
  const cenaZaKsSDph = Number(nakup?.cenaZaKsSDph || 0);
  const celkemSDph = Math.round(mnozstvi * cenaZaKsSDph * 100) / 100;

  return {
    mnozstvi,
    cenaZaKsSDph,
    celkemSDph,
  };
}

function buildProdejItemFromNakup(nakup) {
  const nazev = String(nakup?.nazev || "").trim();
  if (!nazev) return null;

  const mnozstvi = Number(nakup?.mnozstvi || 1);
  const nakupCenaZaKsSDph = Number(nakup?.cenaZaKsSDph || 0);

  const marzeProc = 50;
  const sazbaDph = 21;

  const prodejCenaZaKsSDph = Math.round(nakupCenaZaKsSDph * (1 + marzeProc / 100) * 100) / 100;
  const prodejCenaZaKsBezDph = Math.round((prodejCenaZaKsSDph / (1 + sazbaDph / 100)) * 100) / 100;

  return {
    id: `nakup_item_${nakup.id}`,
    sourceNakupId: String(nakup.id || ""),
    cenikId: "interni_nakup",
    nazev,
    skupina: "Interně nakoupené zboží",
    jednotka: "ks",
    mnozstvi,
    cenaBezDph: prodejCenaZaKsBezDph,
    dph: sazbaDph,
  };
}

const interniSouctyPreview = useMemo(() => {
    return calcInterniSoucty(form.nakupy || [], souctyPreview.sDph);
  }, [form.nakupy, souctyPreview.sDph]);

  const previewFakturace = useMemo(() => {
    return (form.nakupy || [])
      .filter((it) => !!it?.zahrnoutDoFaktury)
      .map((it) => {
        const built = buildProdejItemFromNakup(it);
        if (!built) return null;
        const row = calcRow(built);
        return {
          id: it.id,
          nazev: built.nazev,
          mnozstvi: built.mnozstvi,
          bez: row.bez,
          dph: row.dph,
          sdph: row.sdph,
        };
      })
      .filter(Boolean);
  }, [form.nakupy]);

  async function uploadOne(file, kind) {
    const fd = new FormData();
    fd.append("files", file);
    fd.append("orderId", String(detail?.id || ""));
    fd.append("kategorie", kind === "pred" ? "fotoPred" : "fotoPo");

    console.log("UPLOAD_DEBUG", {
      fileName: file?.name || "",
      kind,
      orderId: String(detail?.id || ""),
      kategorie: kind === "pred" ? "fotoPred" : "fotoPo",
    });

    const res = await apiFetch(`/api/foto/upload`, {
      method: "POST",
      body: fd,
    });

    const data = await res.json();
    const first = Array.isArray(data?.files) ? data.files[0] : null;
    const url = first?.url || data?.url || "";

    if (!data?.ok || !url) {
      throw new Error(data?.error || "Upload fotky selhal");
    }

    return url;
  }

  async function saveWhole(nextItem) {
    const prevStav = String(detail?.stav || "").trim();
    const nextStav = String(nextItem?.stav || "").trim();

    const isNewVyfakturovano =
      nextStav === "vyfakturovano" &&
      prevStav !== "vyfakturovano";

    const data = isNewVyfakturovano
      ? await apiJson(`${API}/zakazky/${detail.id}/vyfakturovano`, {
          method: "POST",
          body: {
            datumVyfakturovani: nextItem?.datumVyfakturovani || new Date().toISOString(),
          },
        })
      : await apiJson(`${API}/zakazky/${detail.id}`, {
          method: "PUT",
          body: nextItem,
        });

    if (!data?.ok || !data?.item) throw new Error(data?.error || "Uložení zakázky selhalo");

    const mergedItem = isNewVyfakturovano
      ? { ...nextItem, ...data.item }
      : data.item;

    setDetail(mergedItem);
    setForm(mergedItem);
    try {
      if (typeof onSaved === "function") onSaved(mergedItem);
    } catch {}
    return mergedItem;
  }

  async function saveUpdatedPhotos(kindKey, urlsToAdd) {
    const currentStatus = String(detail?.stav || "").trim();
    const shouldMoveToRozpracovano =
      kindKey === "fotoPred" &&
      Array.isArray(urlsToAdd) &&
      urlsToAdd.length > 0 &&
      currentStatus !== "dokonceno" &&
      currentStatus !== "vyfakturovano" &&
      currentStatus !== "zruseno";

    const nextItem = {
      ...detail,
      [kindKey]: [...(detail[kindKey] || []), ...urlsToAdd],
      ...(shouldMoveToRozpracovano ? { stav: "rozpracovano", datumRozpracovani: new Date().toISOString() } : {}),
    };
    await saveWhole(nextItem);
  }

  async function removePhoto(kindKey, url) {
    try {
      const nextItem = {
        ...detail,
        [kindKey]: (detail[kindKey] || []).filter((x) => x !== url),
      };
      await saveWhole(nextItem);
    } catch (err) {
      alert(err?.message || "Smazání fotky selhalo");
    }
  }

  const onPickPred = async (e) => {
    const files = Array.from(e.target.files || []);
    alert("PICK_PRED count=" + files.length + " id=" + String(detail?.id || ""));
    console.log("PICK_PRED_DEBUG", {
      count: files.length,
      names: files.map(f => f?.name || ""),
      detailId: String(detail?.id || ""),
    });
    if (!files.length) return;
    try {
      setUploadingPred(true);
      const urls = [];
      for (const f of files) urls.push(await uploadOne(f, "pred"));
      await saveUpdatedPhotos("fotoPred", urls);
    } catch (err) {
      alert(err?.message || "Upload foto před selhal");
    } finally {
      setUploadingPred(false);
      if (predRef.current) predRef.current.value = "";
    }
  };

  const onPickPo = async (e) => {
    const files = Array.from(e.target.files || []);
    alert("PICK_PO count=" + files.length + " id=" + String(detail?.id || ""));
    console.log("PICK_PO_DEBUG", {
      count: files.length,
      names: files.map(f => f?.name || ""),
      detailId: String(detail?.id || ""),
    });
    if (!files.length) return;
    try {
      setUploadingPo(true);
      const urls = [];
      for (const f of files) urls.push(await uploadOne(f, "po"));
      await saveUpdatedPhotos("fotoPo", urls);
    } catch (err) {
      alert(err?.message || "Upload foto po selhal");
    } finally {
      setUploadingPo(false);
      if (poRef.current) poRef.current.value = "";
    }
  };

  async function saveQuickMoveDate() {
    const picked = String(quickMoveDate || "").trim();
    if (!picked) {
      alert("Vyber nové datum.");
      return;
    }

    try {
      setSaving(true);

      const nextPayload = {
        ...detail,
        ...form,
        manualniPresunTerminu: true,
        planovaneDatum: picked,
        dalsiNavsteva: isJednorazova ? "" : picked,
        calendarDate: picked,
      };

      const savedItem = await saveWhole(nextPayload);
      const mergedSaved = {
        ...savedItem,
        planovaneDatum: picked,
        dalsiNavsteva: isJednorazova ? "" : picked,
        calendarDate: picked,
      };
      setDetail(mergedSaved);
      setForm(mergedSaved);
      setQuickMoveDate(String(picked).slice(0, 10));
      try {
        if (typeof onSaved === "function") onSaved(mergedSaved);
      } catch {}
      setSuccessMessage(`Termín uložen: ${String(picked).slice(0, 10)}`);
      setEditMode(false);
    } catch (err) {
      alert(err?.message || "Přesun termínu selhal.");
    } finally {
      setSaving(false);
    }
  }

  async function restoreAutomaticPlan() {
    try {
      const baseDate = String(
        form?.datumZhotoveni ||
        detail?.datumZhotoveni ||
        form?.posledniNavsteva ||
        detail?.posledniNavsteva ||
        ""
      ).trim();

      const periodicita = String(form?.periodicita || detail?.periodicita || "").trim();
      if (!baseDate) {
        alert("Chybí datum posledního dokončení / poslední návštěvy.");
        return;
      }
      if (!periodicita) {
        alert("Chybí periodicita.");
        return;
      }

      const nextDate = plusPeriodLocal(baseDate, periodicita);
      if (!nextDate) {
        alert("Nepodařilo se spočítat další termín.");
        return;
      }

      const currentPlan = Array.isArray(detail?.planNavstev)
        ? detail.planNavstev.map((x) => String(x || "").slice(0, 10)).filter(Boolean)
        : [];

      const futureCount = Math.max(1, currentPlan.length || 1);
      const rebuiltPlan = [nextDate];
      let current = nextDate;

      for (let i = 1; i < futureCount; i += 1) {
        current = plusPeriodLocal(current, periodicita);
        if (!current) break;
        rebuiltPlan.push(current);
      }

      setSaving(true);

      const nextPayload = {
        ...detail,
        ...form,
        manualniPresunTerminu: true,
        obnovaAutomatickehoPlanu: true,
        planovaneDatum: nextDate,
        dalsiNavsteva: isJednorazova ? "" : nextDate,
        planNavstev: isJednorazova ? [nextDate] : rebuiltPlan,
        calendarDate: nextDate,
      };

      const savedItem = await saveWhole(nextPayload);
      const mergedSaved = {
        ...savedItem,
        planovaneDatum: nextDate,
        dalsiNavsteva: isJednorazova ? "" : nextDate,
        planNavstev: isJednorazova ? [nextDate] : rebuiltPlan,
        calendarDate: nextDate,
      };
      setDetail(mergedSaved);
      setForm(mergedSaved);
      setQuickMoveDate(String(nextDate).slice(0, 10));
      try {
        if (typeof onSaved === "function") onSaved(mergedSaved);
      } catch {}
      setSuccessMessage(`Obnoven automatický plán: ${String(nextDate).slice(0, 10)}`);
      setEditMode(false);
    } catch (err) {
      alert(err?.message || "Obnova automatického plánu selhala.");
    } finally {
      setSaving(false);
    }
  }

  async function saveNakupyOnly(nextNakupy) {
    try {
      const cleanedNakupy = (nextNakupy || []).map((it) => ({
        ...it,
        celkemSDph: Math.round(Number(it.cenaZaKsSDph || 0) * Number(it.mnozstvi || 1) * 100) / 100,
      }));

      const manualItems = Array.isArray(form?.items)
        ? form.items.filter((it) => !it?.sourceNakupId)
        : [];

      const generatedItems = cleanedNakupy
        .filter((it) => !!it?.zahrnoutDoFaktury)
        .map((it) => buildProdejItemFromNakup(it))
        .filter(Boolean);

      const nextPayload = {
        ...detail,
        ...form,
        nakupy: cleanedNakupy,
        items: [...manualItems, ...generatedItems],
      };

      await saveWhole(nextPayload);
    } catch (err) {
      alert(err?.message || "Uložení interních nákladů selhalo");
    }
  }
  function addNakupRow() {
    if (!String(nakupDraft.nazev || "").trim()) {
      alert("Vyplň název nákupu.");
      return;
    }

    const row = {
      id: `nakup_${Date.now()}`,
      nazev: String(nakupDraft.nazev || "").trim(),
      dodavatel: String(nakupDraft.dodavatel || "").trim(),
      datum: String(nakupDraft.datum || "").slice(0, 10),
      mnozstvi: Number(nakupDraft.mnozstvi || 1),
      cenaZaKsSDph: Number(nakupDraft.cenaZaKsSDph || 0),
      poznamka: String(nakupDraft.poznamka || "").trim(),
      zahrnoutDoFaktury: !!nakupDraft.zahrnoutDoFaktury,
      popisDoFaktury: String(nakupDraft.popisDoFaktury || "").trim(),
      celkemSDph: Math.round(Number(nakupDraft.mnozstvi || 1) * Number(nakupDraft.cenaZaKsSDph || 0) * 100) / 100,
    };

    setForm((prev) => ({
      ...prev,
      nakupy: [...(prev.nakupy || []), row],
    }));

    setNakupDraft({
      nazev: "",
      dodavatel: "",
      datum: new Date().toISOString().slice(0, 10),
      mnozstvi: 1,
      cenaZaKsSDph: "",
      poznamka: "",
      zahrnoutDoFaktury: true,
      popisDoFaktury: "",
    });
  }

  function removeNakupRow(id) {
    setForm((prev) => ({
      ...prev,
      nakupy: (prev.nakupy || []).filter((x) => x.id !== id),
    }));
  }

  function toggleNakupDoFaktury(id) {
    setForm((prev) => ({
      ...prev,
      nakupy: (prev.nakupy || []).map((x) =>
        x.id === id ? { ...x, zahrnoutDoFaktury: !x.zahrnoutDoFaktury } : x
      ),
    }));
  }

  function updateNakupPopis(id, value) {
    setForm((prev) => ({
      ...prev,
      nakupy: (prev.nakupy || []).map((x) =>
        x.id === id ? { ...x, popisDoFaktury: value } : x
      ),
    }));
  }

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const updateItem = (id, patch) => {
    setForm((prev) => ({
      ...prev,
      items: (prev.items || []).map((x) => (x.id === id ? { ...x, ...patch } : x)),
    }));
  };

  const removeItem = (id) => {
    setForm((prev) => ({
      ...prev,
      items: (prev.items || []).filter((x) => x.id !== id),
    }));
  };

  const addItemFromCenik = () => {
    const it = cenik.find((x) => String(x.id) === String(selectedCenikId));
    if (!it) return;

    const isCustom = String(it.id) === "jine";

    const newItem = {
      id: `${it.id}_${Date.now()}`,
      cenikId: it.id,
      nazev: isCustom ? String(customName || "Jiné") : it.nazev,
      skupina: it.skupina || "",
      jednotka: it.jednotka || "zakázka",
      mnozstvi: 1,
      cenaBezDph: isCustom ? Number(customPrice || 0) : Number(it.cenaBezDph || 0),
      dph: Number(it.dph || 21),
    };

    setForm((prev) => ({
      ...prev,
      items: [...(prev.items || []), newItem],
    }));
  };

  async function addToCalendar() {
    try {
      setCalendarLoading(true);
      const data = await apiJson(`${API}/kalendar/vytvor-zakazka/${detail.id}`, {
        method: "POST",
        body: {},
      });
      if (!data?.ok || !data?.item) throw new Error(data?.error || "Zápis do kalendáře selhal");
      setDetail(data.item);
      setForm(data.item);
      alert("Zakázka byla zapsána do kalendáře.");
    } catch (err) {
      alert(err?.message || "Zápis do kalendáře selhal");
    } finally {
      setCalendarLoading(false);
    }
  }


  async function prepareRenewalEmail() {
    try {
      setEmailLoading(true);
      const data = await apiJson(`${API}/email/obnova/${detail.id}`, { method: "GET" });

      if (!data?.ok) throw new Error(data?.error || "Příprava e-mailu selhala");
      if (!data?.email) throw new Error("Zakázka nemá vyplněný e-mail objednavatele");

      window.location.href = data.mailto;
    } catch (err) {
      alert(err?.message || "Příprava e-mailu selhala");
    } finally {
      setEmailLoading(false);
    }
  }

  const cancelEdit = () => {
    setForm(detail);
    setEditMode(false);
  };

  const saveEdit = async () => {
    try {
      const errors = validateDetailForm(form, isJednorazova, isPredplatne, isBalicek, cenaCelkemNum, uhrazenoCelkemNum);
      if (errors.length) {
        alert(errors.join("\n"));
        return;
      }

      setSaving(true);

      const typPece = String(form?.typPece || "").trim();

      const payload = {
        ...detail,
        ...form,
        id: detail?.id,
        createdAt: detail?.createdAt,
        cenaCelkem: cenaCelkemNum,
        soucty: souctyReal,
        zalohaCastka: zalohaCastkaNum,
        doplatekCastka: doplatekCastkaNum,
        uhrazenoCelkem: uhrazenoCelkemNum,
        zaplaceno: financniStavAuto === "zaplaceno",
        financniStav: financniStavAuto,
        stavPredplatneho:
          typPece === "jednorazova"
            ? ""
            : String(form?.stavPredplatneho || detail?.stavPredplatneho || "aktivni"),
        dalsiNavsteva:
          typPece === "jednorazova"
            ? ""
            : String(form?.dalsiNavsteva || ""),
        uhrazenoDo:
          typPece === "predplatne"
            ? String(form?.uhrazenoDo || "")
            : "",
        periodicita:
          typPece === "jednorazova"
            ? ""
            : String(form?.periodicita || ""),
        pocetNavstevCelkem:
          typPece === "balicek"
            ? (Array.isArray(form?.planNavstev) ? form.planNavstev.length : Number(form?.pocetNavstevCelkem || 0))
            : Number(form?.pocetNavstevCelkem || 0),
        zbyvaNavstev:
          typPece === "balicek"
            ? (Array.isArray(form?.planNavstev) ? form.planNavstev.length : Number(form?.zbyvaNavstev || 0))
            : Number(form?.zbyvaNavstev || 0),
        opakovana: typPece === "jednorazova" ? false : !!form?.opakovana,
      };

      const savedItem = await saveWhole(payload);
      setDetail(savedItem);
      setForm(savedItem);
      setEditMode(false);

      try {
        if (typeof onSaved === "function") onSaved(savedItem);
      } catch {}

      alert("Změny byly uloženy.");
    } catch (err) {
      console.error("saveEdit error:", err);
      alert(err?.message || "Uložení změn selhalo.");
    } finally {
      setSaving(false);
    }
  };

  const previewRenewEmail = async () => {
    try {
      setRenewEmailPreviewLoading(true);
      const data = await apiJson(`${API}/email/obnova/${detail.id}`, { method: "GET" });
  
      if (!data?.ok) {
        throw new Error(data?.error || "Příprava e-mailu selhala");
      }
  
      if (!data?.email) {
        throw new Error("Objednavatel nemá vyplněný e-mail");
      }
  
      setRenewEmailPreview({
        email: data.email || "",
        subject: data.subject || "",
        body: data.body || "",
      });
    } catch (err) {
      alert(err?.message || "Příprava e-mailu selhala");
    } finally {
      setRenewEmailPreviewLoading(false);
    }
  };
  
  const prepareRenewEmail = async () => {
    try {
      setRenewEmailLoading(true);
      const data = await apiJson(`${API}/email/obnova/odeslat/${detail.id}`, {
        method: "POST",
        body: {
          subject: renewEmailPreview?.subject || "",
          body: renewEmailPreview?.body || "",
        },
      });
  
      if (!data?.ok) {
        throw new Error(data?.error || "Odeslání e-mailu selhalo");
      }
  
      alert("E-mail k obnovení byl odeslán.");
    } catch (err) {
      alert(err?.message || "Odeslání e-mailu selhalo");
    } finally {
      setRenewEmailLoading(false);
    }
  };
  
    const previewDoneEmail = async () => {
      try {
        setDoneEmailPreviewLoading(true);
        const data = await apiJson(`${API}/email/dokonceno/${detail.id}`, { method: "GET" });

        if (!data?.ok) {
          throw new Error(data?.error || "Náhled e-mailu o dokončení selhal");
        }

        if (!data?.email) {
          throw new Error("Zakázka nemá vyplněný e-mail objednavatele");
        }

        const rawBody = String(data.body || "");
        const cleanedBody = rawBody
          .split("\n")
          .filter((line) => {
            const x = String(line || "").trim();
            if (!x) return true;
            if (x === "Fotodokumentace:") return false;
            if (x === "Před:") return false;
            if (x === "Po:") return false;
            if (x.startsWith("/api/udrzba/foto/")) return false;
            if (x.startsWith("http://") || x.startsWith("https://")) return false;
            return true;
          })
          .join("\n");

        setDoneEmailPreview({
          email: data.email || "",
          subject: data.subject || "",
          body: cleanedBody,
          fotoPred: Array.isArray(data.fotoPred) ? data.fotoPred : [],
          fotoPo: Array.isArray(data.fotoPo) ? data.fotoPo : [],
        });
      } catch (err) {
        alert(err?.message || "Náhled e-mailu o dokončení selhal");
      } finally {
        setDoneEmailPreviewLoading(false);
      }

  };

    const prepareDoneEmail = async () => {
      try {
        setDoneEmailLoading(true);
        const data = await apiJson(`${API}/email/dokonceno/odeslat/${detail.id}`, {
          method: "POST",
          body: {
            subject: doneEmailPreview?.subject || "",
            body: doneEmailPreview?.body || "",
          },
        });

        if (!data?.ok) {
          throw new Error(data?.error || "Odeslání e-mailu o dokončení selhalo");
        }

        alert("E-mail o dokončení byl odeslán.");
      } catch (err) {
        alert(err?.message || "Odeslání e-mailu o dokončení selhalo");
      } finally {
        setDoneEmailLoading(false);
      }
    };

    const printReportPredPo = () => {
      const d = detail || {};
      const fotoPred = Array.isArray(d.fotoPred) ? d.fotoPred : [];
      const fotoPo = Array.isArray(d.fotoPo) ? d.fotoPo : [];
      const items = Array.isArray(d.items) ? d.items : [];

      const rows = items.map((it) => {
        const nazev = String(it?.nazev || "-");
        const mnozstvi = Number(it?.mnozstvi || 0);
        const bez = Number(it?.radekBezDph || 0).toLocaleString("cs-CZ");
        const dph = Number(it?.radekDph || 0).toLocaleString("cs-CZ");
        const sdph = Number(it?.radekSDph || 0).toLocaleString("cs-CZ");
        return `
          <tr>
            <td>${nazev}</td>
            <td style="text-align:center">${mnozstvi}</td>
            <td style="text-align:right">${bez} Kč</td>
            <td style="text-align:right">${dph} Kč</td>
            <td style="text-align:right">${sdph} Kč</td>
          </tr>
        `;
      }).join("");

      const predHtml = fotoPred.length
        ? fotoPred.map((url) => `<div class="imgCard"><img src="${url}" alt="" loading="eager" referrerpolicy="no-referrer"><div class="cap">Před</div></div>`).join("")
        : `<div class="muted">Bez fotek před.</div>`;

      const poHtml = fotoPo.length
        ? fotoPo.map((url) => `<div class="imgCard"><img src="${url}" alt="" loading="eager" referrerpolicy="no-referrer"><div class="cap">Po</div></div>`).join("")
        : `<div class="muted">Bez fotek po.</div>`;

      const baseHtml = (withPhotos) => `
        <!doctype html>
        <html lang="cs">
        <head>
          <meta charset="utf-8" />
          <title>Report před/po – ${String(d.id || "")}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; color: #111827; }
            h1,h2 { margin: 0 0 12px; }
            .top { margin-bottom: 20px; }
            .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px 20px; margin-bottom: 20px; }
            .section { margin-bottom: 24px; }
            .photos { display: grid; grid-template-columns: repeat(2, minmax(0,1fr)); gap: 12px; }
            .imgCard { border: 1px solid #e5e7eb; border-radius: 10px; padding: 8px; background: #fff; page-break-inside: avoid; }
            .imgCard img { width: 100%; max-height: 180px; object-fit: cover; border-radius: 8px; display:block; }
            .cap { margin-top: 6px; font-size: 12px; color: #4b5563; }
            .muted { color: #6b7280; }
            table { width: 100%; border-collapse: collapse; }
            th, td { border: 1px solid #e5e7eb; padding: 8px; font-size: 14px; }
            th { background: #f8fafc; text-align: left; }
            .sum { margin-top: 12px; }
            .toolbar { display:flex; gap:8px; flex-wrap:wrap; margin-bottom:16px; }
            @media print {
              body { margin: 12px; }
              .toolbar { display: none; }
            }
          </style>
        </head>
        <body>
          <div class="toolbar">
            <button type="button" onclick="window.print()">Tisknout / uložit do PDF</button>
            <button type="button" onclick="window.close()">Zavřít</button>
          </div>

          <div class="top">
            <h1>Report zakázky před / po</h1>
            <div style="margin-top:8px;">ID zakázky: <strong>${String(d.id || "-")}</strong></div>
          </div>

          <div class="grid">
            <div><strong>Objednavatel:</strong> ${String(d.objednavatel || "-")}</div>
            <div><strong>Telefon:</strong> ${String(d.telefon || "-")}</div>
            <div><strong>E-mail:</strong> ${String(d.email || "-")}</div>
            <div><strong>Číslo hrobu:</strong> ${String(d.cisloHrobu || "-")}</div>
            <div><strong>Hřbitov:</strong> ${String(d.hrbitov || "-")}</div>
            <div><strong>Stav:</strong> ${String(d.stav || "-")}</div>
            <div><strong>Datum objednání:</strong> ${String(d.datumObjednani || "-")}</div>
            <div><strong>Datum zhotovení:</strong> ${String(d.datumZhotoveni || "-")}</div>
          </div>

          <div class="section">
            <h2>Zadání práce</h2>
            <div>${String(d.zadaniPrace || "-")}</div>
          </div>

          ${
            withPhotos ? `
            <div class="section">
              <h2>Fotodokumentace před</h2>
              <div class="photos">${predHtml}</div>
            </div>

            <div class="section">
              <h2>Fotodokumentace po</h2>
              <div class="photos">${poHtml}</div>
            </div>
            ` : `
            <div class="section">
              <h2>Fotodokumentace</h2>
              <div class="muted">Lehký tiskový režim bez fotek.</div>
            </div>
            `
          }

          <div class="section">
            <h2>Služby</h2>
            ${
              rows
                ? `<table>
                    <thead>
                      <tr>
                        <th>Název</th>
                        <th>Množství</th>
                        <th>Bez DPH</th>
                        <th>DPH</th>
                        <th>S DPH</th>
                      </tr>
                    </thead>
                    <tbody>${rows}</tbody>
                  </table>`
                : `<div class="muted">Bez položek.</div>`
            }
            <div class="sum">
              <div><strong>Součet bez DPH:</strong> ${Number(d?.cenaBezDph ?? d?.soucty?.bezDph ?? 0).toLocaleString("cs-CZ")} Kč</div>
              <div><strong>DPH:</strong> ${Number(d?.cenaDph ?? d?.soucty?.dph ?? 0).toLocaleString("cs-CZ")} Kč</div>
              <div><strong>Celkem s DPH:</strong> ${Number(d?.cenaCelkem ?? d?.soucty?.sDph ?? 0).toLocaleString("cs-CZ")} Kč</div>
            </div>
          </div>
        </body>
        </html>
      `;

      const choice = window.confirm(
        `Chceš otevřít report S FOTKAMI?

OK = s fotkami
Storno = lehký report bez fotek`
      );

      const w = window.open("", "_blank", "width=1200,height=900");
      if (!w) {
        alert("Nepodařilo se otevřít okno pro tisk reportu.");
        return;
      }

      w.document.open();
      w.document.write(baseHtml(choice));
      w.document.close();
    };
    const markInvoiced = async () => {
      if (!detail?.id) return;
      try {
        setSaving(true);

        const d = await apiJson(`${API}/zakazky/${detail.id}/vyfakturovano`, {
          method: "POST",
          body: {},
        });

        if (!d?.ok || !d?.item) {
          throw new Error(d?.error || "Nepodařilo se označit jako vyfakturováno.");
        }

        setDetail(d.item);
        setForm(d.item);
        try {
          if (typeof onSaved === "function") onSaved(d.item);
        } catch {}

        alert("Zakázka byla označena jako vyfakturovaná.");
      } catch (err) {
        console.error("markInvoiced error:", err);
        alert(err?.message || "Nepodařilo se označit jako vyfakturováno.");
      } finally {
        setSaving(false);
      }
    };

    const markPaid = async () => {
      if (!detail?.id) return;
      try {
        setSaving(true);

        const d = await apiJson(`${API}/zakazky/${detail.id}/zaplaceno`, {
          method: "POST",
          body: {},
        });

        if (!d?.ok || !d?.item) {
          throw new Error(d?.error || "Nepodařilo se uložit platbu.");
        }

        setDetail(d.item);
        setForm(d.item);
        try {
          if (typeof onSaved === "function") onSaved(d.item);
        } catch {}

        alert("Platba byla zaevidována.");
      } catch (err) {
        console.error("markPaid error:", err);
        alert(err?.message || "Nepodařilo se uložit platbu.");
      } finally {
        setSaving(false);
      }
    };

        const markDone = async () => {
          if (!detail?.id) return;
          try {
            setSaving(true);

            const d = await apiJson(`${API}/zakazky/${detail.id}/dokonceno`, {
              method: "POST",
              body: {},
            });

            if (!d?.ok || !d?.item) {
              throw new Error(d?.error || "Nepodařilo se označit jako dokončeno.");
            }

            setDetail(d.item);
            setForm(d.item);
            try {
              if (typeof onSaved === "function") onSaved(d.item);
            } catch {}

            const nextDateDone = String(d?.item?.dalsiNavsteva || d?.item?.planovaneDatum || "").trim();
            const msgDone = nextDateDone
              ? `Návštěva byla dokončena. Další termín: ${formatDate(nextDateDone)}`
              : "Návštěva byla dokončena.";

            alert(msgDone);
          } catch (err) {
            console.error("markDone error:", err);
            alert(err?.message || "Nepodařilo se označit jako dokončeno.");
          } finally {
            setSaving(false);
          }
        };





  const confirmRenewal = async () => {
    if (!detail?.id) return;
    try {
      setSaving(true);

      const today = new Date().toISOString().slice(0, 10);
      const plusYear = (dateStr) => {
        const src = String(dateStr || "").slice(0, 10);
        if (!src) return "";
        const [y, m, d] = src.split("-").map(Number);
        if (!y || !m || !d) return "";
        return `${String(y + 1).padStart(4, "0")}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      };

      const currentType = String(detail?.typPece || "").trim();
      const nextType = !currentType || currentType === "jednorazova" ? "predplatne" : currentType;
      const nextPeriodicita = String(detail?.periodicita || "").trim() || "rocne";
      const nextDalsiNavsteva = String(detail?.dalsiNavsteva || "").trim() || today;
      const nextUhrazenoDo = String(detail?.uhrazenoDo || "").trim() || plusYear(today);

        const renewalPrice = Math.round(Number(detail?.cenaCelkem ?? detail?.soucty?.sDph ?? 0) * 100) / 100;

        const payload = {
          ...detail,
          typPece: nextType,
          opakovana: true,
          periodicita: nextPeriodicita,
          obnovaPripravena: true,
          stavObnovy: "potvrzena",
          stavPredplatneho: "ceka_na_realizaci",
          datumPotvrzeniObnovy: detail?.datumPotvrzeniObnovy || today,
          dalsiNavsteva: nextDalsiNavsteva,
          planovaneDatum: nextDalsiNavsteva,
          uhrazenoDo: nextUhrazenoDo,
          cenaCelkem: renewalPrice,
          doplatekCastka: renewalPrice,
          uhrazenoCelkem: 0,
          zalohaCastka: 0,
          financniStav: "nezaplaceno",
          zaplaceno: false,
        };

      const saved = await apiJson(`${API}/zakazky/${detail.id}`, {
        method: "PUT",
        body: payload,
      });

      if (!saved?.ok) {
        throw new Error(saved?.error || "Nepodařilo se potvrdit obnovu.");
      }

      const data = await apiJson(`${API}/zakazky/${detail.id}`, {
        method: "GET",
      });
      if (!data?.ok || !data?.item) {
        throw new Error("Obnova se uložila, ale nepodařilo se načíst detail.");
      }

      setDetail(data.item);
      setForm(data.item);
      try {
        if (typeof onSaved === "function") onSaved(data.item);
      } catch {}

      alert("Obnova byla potvrzena.");
    } catch (err) {
      console.error("confirmRenewal error:", err);
      alert(err?.message || "Nepodařilo se potvrdit obnovu.");
    } finally {
      setSaving(false);
    }
  };

  const renewState = String(detail?.stavObnovy || "").trim();
  const isRepeatCare =
    !!detail?.opakovana ||
    String(detail?.typPece || "").trim() === "predplatne" ||
    String(detail?.typPece || "").trim() === "balicek" ||
    String(detail?.typPece || "").trim() === "balicek_4x";
  
  const canShowRenewConfirm =
    !editMode &&
    !!detail?.obnovaPripravena &&
    renewState !== "potvrzena" &&
    renewState !== "realizovana";
  
  const canShowRenewEmail =
    !editMode &&
    isRepeatCare &&
    (
      !!detail?.obnovaPripravena ||
      renewState === "potvrzena" ||
      renewState === "realizovana" ||
      String(detail?.stavPredplatneho || "").trim() === "ceka_na_obnovu" ||
      String(detail?.stavPredplatneho || "").trim() === "ceka_na_realizaci" ||
      String(detail?.stavPredplatneho || "").trim() === "aktivni"
    );
  
  const canShowDoneEmail =
    !editMode &&
    (
      renewState === "realizovana" ||
      String(detail?.stav || "").trim() === "dokonceno" ||
      (
        !!detail?.datumZhotoveni &&
        String(detail?.typPece || "").trim() !== "jednorazova"
      )
    );

    const detailTypPece = String(detail?.typPece || "").trim();
      const detailJeNavaznaPece =
        detailTypPece === "predplatne" ||
        detailTypPece === "balicek" ||
        detailTypPece === "balicek_4x" ||
        !!detail?.opakovana;

      const detailStav = String(detail?.stav || "").trim();
      const detailFinancniStav = String(detail?.financniStav || "").trim();
      const detailStavPredplatneho = String(detail?.stavPredplatneho || "").trim();
      const detailStavObnovy = String(detail?.stavObnovy || "").trim();

      const canShowInvoiced =
        !editMode &&
        detailStav !== "vyfakturovano" &&
        (
          detailStav === "dokonceno" ||
          (!detailJeNavaznaPece && detailFinancniStav === "zaplaceno")
        );

      const todayIso = new Date().toISOString().slice(0, 10);
      const todayVisitAlreadyDone =
        detailJeNavaznaPece &&
        Array.isArray(detail?.navstevy) &&
        detail.navstevy.some((n) => String(n?.datumSkutecne || "").trim() === todayIso);

      const canShowPaid =
        !editMode &&
        detailFinancniStav !== "zaplaceno" && (
          (!detailJeNavaznaPece && (
            detailStav === "dokonceno" ||
            detailStav === "vyfakturovano"
          )) ||
          (detailJeNavaznaPece && (
            detailStavPredplatneho === "ceka_na_realizaci" ||
            detailStavObnovy === "potvrzena" ||
            detailStav === "vyfakturovano"
          ))
        );

      const canShowDone =
        !editMode &&
        !todayVisitAlreadyDone &&
        detailStav !== "dokonceno" &&
        (
          (
            detailJeNavaznaPece &&
            detailStav === "vyfakturovano"
          ) ||
          detailStavPredplatneho === "ceka_na_realizaci" ||
          detailStavObnovy === "potvrzena"
        );

      const renewalPrice = Math.round(Number(detail?.cenaCelkem ?? detail?.soucty?.sDph ?? 0) * 100) / 100;
    const renewalDoplatek = Math.round(Number(detail?.doplatekCastka ?? renewalPrice) * 100) / 100;
      const renewalWaitingPayment = String(detail?.stavPredplatneho || "").trim() === "ceka_na_realizaci";
      const renewalNeedsAction =
        String(detail?.stavPredplatneho || "").trim() === "ceka_na_obnovu" ||
        String(detail?.stavObnovy || "").trim() === "potvrzena" ||
        renewalWaitingPayment ||
        !!detail?.obnovaPripravena;

      const nextStepHint =
        renewalWaitingPayment
          ? "Další krok: zaevidovat platbu obnovy."
          : String(detail?.stavPredplatneho || "").trim() === "ceka_na_obnovu"
            ? "Další krok: připravit a potvrdit obnovu."
            : (
                String(detail?.financniStav || "").trim() === "zaplaceno" &&
                String(detail?.stav || "").trim() !== "vyfakturovano"
              )
              ? "Další krok: vyfakturovat zakázku."
              : (
                  String(detail?.stav || "").trim() === "dokonceno" &&
                  String(detail?.financniStav || "").trim() !== "zaplaceno"
                )
                ? "Další krok: zaevidovat platbu."
                : "";

  const quickActionButtonStyle = {
    padding: "10px 14px",
    borderRadius: 12,
    border: "1px solid #dbe3ee",
    background: "#ffffff",
    color: "#0f172a",
    fontSize: 14,
    fontWeight: 700,
    cursor: "pointer",
    boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
  };

  const renewalActionButtonStyle = {
    padding: "10px 14px",
    borderRadius: 12,
    border: "1px solid #f5d08a",
    background: "#ffffff",
    color: "#92400e",
    fontSize: 14,
    fontWeight: 700,
    cursor: "pointer",
    boxShadow: "0 1px 2px rgba(146,64,14,0.06)",
  };

  const emailPreviewCardStyle = {
    marginBottom: 18,
    padding: 16,
    border: "1px solid #dbe3ee",
    borderRadius: 16,
    background: "#f8fafc",
    boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
  };

  const emailPreviewInputStyle = {
    width: "100%",
    marginBottom: 10,
    padding: "10px 12px",
    border: "1px solid #d1d5db",
    borderRadius: 10,
    fontSize: 14,
    boxSizing: "border-box",
    background: "#fff",
  };

  const emailPreviewTextareaStyle = {
    width: "100%",
    minHeight: 220,
    resize: "vertical",
    fontSize: 14,
    lineHeight: 1.5,
    background: "#fff",
    border: "1px solid #dbe3ee",
    borderRadius: 12,
    padding: 10,
    boxSizing: "border-box",
  };

  const emailPreviewImageStyle = {
    width: 140,
    height: 140,
    objectFit: "cover",
    borderRadius: 12,
    border: "1px solid #e5e7eb",
    display: "block",
    background: "#fff",
    boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
  };

  const detailReadCardStyle = {
    ...sectionCardStyle,
    borderRadius: 16,
    padding: "16px 18px",
    boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15,23,42,0.45)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1000,
        padding: 20,
      }}
    >
      <div
        style={{
          width: "min(1100px, 100%)",
          maxHeight: "90vh",
          overflow: "auto",
          background: "#fff",
          borderRadius: 20,
          padding: 20,
          boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 14,
            marginBottom: 18,
            paddingBottom: 14,
            borderBottom: "1px solid #e5e7eb",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: 16,
              flexWrap: "wrap",
            }}
          >
            <div style={{ minWidth: 260 }}>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: "#64748b",
                  marginBottom: 6,
                }}
              >
                Údržba hrobu CK
              </div>

              <div style={{ fontSize: 28, fontWeight: 800, lineHeight: 1.1 }}>
                {editMode ? "Úprava zakázky" : "Detail zakázky"}
              </div>

              <div style={{ color: "#6b7280", marginTop: 6, fontSize: 14 }}>
                {detail.id}
              </div>

              <div
                style={{
                  display: "flex",
                  gap: 8,
                  flexWrap: "wrap",
                  marginTop: 12,
                }}
              >
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    padding: "6px 10px",
                    borderRadius: 999,
                    background: "#f8fafc",
                    border: "1px solid #e2e8f0",
                    fontSize: 13,
                    fontWeight: 700,
                    color: "#334155",
                  }}
                >
                  Stav: {stavLabel(detail?.stav)}
                </span>

                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    padding: "6px 10px",
                    borderRadius: 999,
                    background: "#f8fafc",
                    border: "1px solid #e2e8f0",
                    fontSize: 13,
                    fontWeight: 700,
                    color: "#334155",
                  }}
                >
                  Platba: {financniStavLabel(detail?.financniStav)}
                </span>

                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    padding: "6px 10px",
                    borderRadius: 999,
                    background: "#f8fafc",
                    border: "1px solid #e2e8f0",
                    fontSize: 13,
                    fontWeight: 700,
                    color: "#334155",
                  }}
                >
                  Typ péče: {typPeceLabel(detail?.typPece)}
                </span>
              </div>
            </div>

            <div
              style={{
                minWidth: 280,
                flex: "1 1 420px",
                padding: 14,
                border: "1px solid #e5e7eb",
                borderRadius: 16,
                background: "#f8fafc",
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 700, color: "#475569", marginBottom: 10 }}>
                Rychlé akce
              </div>

              <div
                style={{
                  display: "flex",
                  gap: 10,
                  flexWrap: "wrap",
                  alignItems: "center",
                }}
              >
                {!editMode ? (
                  <button type="button" onClick={() => setEditMode(true)} style={quickActionButtonStyle}>Upravit</button>
                ) : (
                  <>
                    <button type="button" onClick={cancelEdit} style={quickActionButtonStyle}>Zrušit</button>
                    <button type="button" onClick={saveEdit} disabled={saving} style={quickActionButtonStyle}>
                      {saving ? "Ukládám…" : "Uložit změny"}
                    </button>
                  </>
                )}

                {canShowPaid && (
                  <button
                    type="button"
                    onClick={markPaid}
                    disabled={saving || editMode}
                    style={quickActionButtonStyle}
                  >
                    {saving ? "Ukládám…" : "Označit jako zaplaceno"}
                  </button>
                )}

                {canShowDone && (
                  <button type="button" onClick={markDone} disabled={saving || editMode} style={quickActionButtonStyle}>
                    {saving ? "Ukládám…" : "Označit jako dokončeno"}
                  </button>
                )}

                {canShowInvoiced && (
                  <button type="button" onClick={markInvoiced} disabled={saving || editMode} style={quickActionButtonStyle}>
                    {saving ? "Ukládám…" : "Označit jako vyfakturováno"}
                  </button>
                )}

                {canShowDoneEmail && (
                  <>
                    <button
                      type="button"
                      style={quickActionButtonStyle}
                      onClick={previewDoneEmail}
                      disabled={doneEmailPreviewLoading || doneEmailLoading || renewEmailLoading || renewEmailPreviewLoading || editMode}
                    >
                      {doneEmailPreviewLoading ? "Načítám náhled…" : "Náhled dokončení"}
                    </button>

                    <button
                      type="button"
                      style={quickActionButtonStyle}
                      onClick={prepareDoneEmail}
                      disabled={doneEmailLoading || doneEmailPreviewLoading || renewEmailLoading || renewEmailPreviewLoading || editMode}
                    >
                      {doneEmailLoading ? "Odesílám…" : "Odeslat informaci o dokončení"}
                    </button>
                  </>
                )}

                <button type="button" onClick={addToCalendar} disabled={calendarLoading || editMode} style={quickActionButtonStyle}>
                  {calendarLoading ? "Ověřuji…" : "Znovu ověřit kalendář"}
                </button>

                <button type="button" onClick={() => alert("Report před/po zatím není napojen.")} disabled={editMode} style={quickActionButtonStyle}>
                  Report před/po
                </button>

                <button type="button" onClick={onClose} style={quickActionButtonStyle}>Zavřít</button>
              </div>
            </div>
          </div>
        </div>

          {renewalNeedsAction ? (
                <div
                  style={{
                    ...sectionCardStyle,
                    border: "2px solid #f59e0b",
                    background: "#fffbeb",
                      marginBottom: 18,
                      boxShadow: "0 0 0 4px rgba(245,158,11,0.18)",
                    }}
                >
                  <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        marginBottom: 12,
                        flexWrap: "wrap",
                      }}
                    >
                      <div style={{ ...sectionTitleStyle, color: "#92400e", marginBottom: 0 }}>Obnova péče</div>
                      <span
                        style={{
                          display: "inline-block",
                          padding: "6px 10px",
                          borderRadius: 999,
                          fontSize: 12,
                          fontWeight: 800,
                          background: "#f59e0b",
                          color: "#fff",
                          letterSpacing: 0.2,
                        }}
                      >
                        Vyžaduje akci
                      </span>
                    </div>

                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                        gap: 12,
                        marginBottom: 14,
                      }}
                    >
                      <div style={topDetailItemStyle}>
                        <div style={topDetailLabelStyle}>Stav obnovy</div>
                        <div style={topDetailValueStyle}>
                          {renewalStatusLabel(detail?.stavObnovy || (detail?.obnovaPripravena ? "pripravena" : "-"))}
                        </div>
                        </div>

                      <div style={topDetailItemStyle}>
                        <div style={topDetailLabelStyle}>Další termín</div>
                        <div style={{ ...topDetailValueStyle, fontWeight: 800 }}>
                          {formatDate(detail?.planovaneDatum || detail?.dalsiNavsteva) || "-"}
                        </div>
                      </div>

                      <div style={topDetailItemStyle}>
                        <div style={topDetailLabelStyle}>Uhrazeno do</div>
                        <div style={{ ...topDetailValueStyle, fontWeight: 800 }}>
                          {formatDate(detail?.uhrazenoDo) || "-"}
                        </div>
                      </div>

                      <div style={topDetailItemStyle}>
                        <div style={topDetailLabelStyle}>Kalendář</div>
                        <div style={topDetailValueStyle}>
                          {detail?.calendarEvent?.uid ? "Zapsáno" : "Nezapsáno"}
                        </div>
                      </div>

                    </div>
                  <div style={{ color: "#78350f", lineHeight: 1.5, marginBottom: 12, fontWeight: 700 }}>
                    {String(detail?.stavPredplatneho || "").trim() === "ceka_na_obnovu"
                        ? "Další krok: poslat obnovení a potvrdit obnovu."
                      : renewalWaitingPayment
                          ? "Další krok: zaevidovat platbu obnovy."
                        : "Obnova je rozpracovaná."}
                  </div>

                    <div
                      style={{
                        display: "flex",
                        gap: 10,
                        flexWrap: "wrap",
                        alignItems: "center",
                      }}
                    >
                        {String(detail?.stavPredplatneho || "").trim() === "ceka_na_obnovu" && (
                          <>
                            <button
                              type="button"
                              style={renewalActionButtonStyle}
                              onClick={previewRenewEmail}
                              disabled={renewEmailPreviewLoading || renewEmailLoading || saving || editMode}
                            >
                              {renewEmailPreviewLoading ? "Načítám náhled…" : "Náhled obnovení"}
                            </button>

                            <button
                              type="button"
                              style={renewalActionButtonStyle}
                              onClick={prepareRenewEmail}
                              disabled={renewEmailLoading || renewEmailPreviewLoading || saving || editMode}
                            >
                              {renewEmailLoading ? "Odesílám…" : "Odeslat e-mail k obnovení"}
                            </button>

                            {canShowRenewConfirm && (
                              <button
                                type="button"
                                style={renewalActionButtonStyle}
                                onClick={confirmRenewal}
                                disabled={saving || editMode}
                              >
                                {saving ? "Potvrzuji…" : "Potvrdit obnovu"}
                              </button>
                            )}
                          </>
                        )}

                          {renewalWaitingPayment && (
                            <button
                              type="button"
                              style={renewalActionButtonStyle}
                              onClick={markPaid}
                              disabled={saving || editMode}
                            >
                              {saving ? "Ukládám…" : "Označit jako zaplaceno"}
                            </button>
                          )}
                    </div>
                </div>
              ) : null}


          {renewEmailPreview && (
            <div
              style={{
                marginBottom: 18,
                padding: 12,
                border: "1px solid #e5e7eb",
                borderRadius: 12,
                background: "#f8fafc",
              }}
            >
              <div style={{ fontWeight: 800, marginBottom: 8 }}>Náhled e-mailu k obnovení</div>
              <div style={{ marginBottom: 6 }}><strong>Komu:</strong> {renewEmailPreview.email || "-"}</div>
          
              <div style={{ marginBottom: 8, fontWeight: 700 }}>Předmět</div>
              <input
                style={{
                  width: "100%",
                  marginBottom: 10,
                  padding: "10px 12px",
                  border: "1px solid #d1d5db",
                  borderRadius: 10,
                  fontSize: 14,
                  boxSizing: "border-box",
                  background: "#fff",
                }}
                value={renewEmailPreview.subject || ""}
                onChange={(e) =>
                  setRenewEmailPreview((prev) => ({
                    ...(prev || {}),
                    subject: e.target.value,
                  }))
                }
                placeholder="Předmět e-mailu"
              />
          
              <div style={{ marginBottom: 8, fontWeight: 700 }}>Text e-mailu</div>
              <textarea
                style={{
                  width: "100%",
                  minHeight: 220,
                  resize: "vertical",
                  fontSize: 14,
                  lineHeight: 1.5,
                  background: "#fff",
                  border: "1px solid #e5e7eb",
                  borderRadius: 10,
                  padding: 10,
                  boxSizing: "border-box",
                }}
                value={renewEmailPreview.body || ""}
                onChange={(e) =>
                  setRenewEmailPreview((prev) => ({
                    ...(prev || {}),
                    body: e.target.value,
                  }))
                }
                placeholder="Text e-mailu"
              />
            </div>
          )}
          
          {doneEmailPreview && (
            <div
              style={{
                marginBottom: 18,
                padding: 12,
                border: "1px solid #e5e7eb",
                borderRadius: 12,
                background: "#f8fafc",
              }}
            >
              <div style={{ fontWeight: 800, marginBottom: 8 }}>Náhled e-mailu o dokončení</div>
              <div style={{ marginBottom: 6 }}><strong>Komu:</strong> {doneEmailPreview.email || "-"}</div>
                <div style={{ marginBottom: 8, fontWeight: 700 }}>Předmět</div>
                <input
                  style={emailPreviewInputStyle}
                  value={doneEmailPreview.subject || ""}
                  onChange={(e) =>
                    setDoneEmailPreview((prev) => ({
                      ...(prev || {}),
                      subject: e.target.value,
                    }))
                  }
                  placeholder="Předmět e-mailu"
                />

                <div style={{ marginBottom: 8, fontWeight: 700 }}>Text e-mailu</div>
                <textarea
                  style={{
                    width: "100%",
                    minHeight: 220,
                    resize: "vertical",
                    fontSize: 14,
                    lineHeight: 1.5,
                    background: "#fff",
                    border: "1px solid #dbe3ee",
                    borderRadius: 12,
                    padding: 10,
                    boxSizing: "border-box",
                  }}
                  value={doneEmailPreview.body || ""}
                  onChange={(e) =>
                    setDoneEmailPreview((prev) => ({
                      ...(prev || {}),
                      body: e.target.value,
                    }))
                  }
                  placeholder="Text e-mailu"
                />

              {!!(doneEmailPreview.fotoPred && doneEmailPreview.fotoPred.length) && (
                <div style={{ marginTop: 12 }}>
                  <div style={{ ...sectionTitleStyle, marginBottom: 8 }}>Fotky před</div>
                  <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                    {doneEmailPreview.fotoPred.map((url, i) => (
                      <a key={`pred_${i}`} href={url} target="_blank" rel="noreferrer">
                        <img
                          src={url}
                          alt={`Foto před ${i + 1}`}
                          style={{
                            width: 140,
                            height: 140,
                            objectFit: "cover",
                            borderRadius: 10,
                            border: "1px solid #e5e7eb",
                            display: "block",
                          }}
                        />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {!!(doneEmailPreview.fotoPo && doneEmailPreview.fotoPo.length) && (
                <div style={{ marginTop: 12 }}>
                  <div style={{ ...sectionTitleStyle, marginBottom: 8 }}>Fotky po</div>
                  <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                    {doneEmailPreview.fotoPo.map((url, i) => (
                      <a key={`po_${i}`} href={url} target="_blank" rel="noreferrer">
                        <img
                          src={url}
                          alt={`Foto po ${i + 1}`}
                          style={{
                            width: 140,
                            height: 140,
                            objectFit: "cover",
                            borderRadius: 10,
                            border: "1px solid #e5e7eb",
                            display: "block",
                          }}
                        />
                      </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          {!editMode ? (
            <>
              {successMessage ? (
                <div
                  style={{
                    marginBottom: 16,
                    padding: "12px 14px",
                    borderRadius: 12,
                    border: "1px solid #86efac",
                    background: "#ecfdf5",
                    color: "#166534",
                    fontWeight: 700,
                    boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
                  }}
                >
                  {successMessage}
                </div>
              ) : null}

                <div
                  style={{
                    ...sectionCardStyle,
                    marginBottom: 18,
                    border: "1px solid #dbeafe",
                    background: "#f8fbff",
                    borderRadius: 16,
                    boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
                    padding: "16px 18px",
                  }}
                >
                  <div style={sectionTitleStyle}>Rychlý přesun termínu</div>

                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                    <input
                      style={{ ...inputStyle, width: 180 }}
                      type="date"
                      value={quickMoveDate || ""}
                      onChange={(e) => setQuickMoveDate(e.target.value)}
                    />

                    <button
                      type="button"
                      style={secondaryButtonStyle}
                      onClick={() => setQuickMoveDate(new Date().toISOString().slice(0, 10))}
                    >
                      Dnes
                    </button>

                    <button
                      type="button"
                      style={secondaryButtonStyle}
                      onClick={() => setQuickMoveDate(shiftIsoDate(new Date().toISOString().slice(0, 10), 1))}
                    >
                      Zítra
                    </button>

                    <button
                      type="button"
                      style={secondaryButtonStyle}
                      onClick={() =>
                        setQuickMoveDate(
                          shiftIsoDate(
                            String(
                              quickMoveDate ||
                              form?.planovaneDatum ||
                              detail?.planovaneDatum ||
                              new Date().toISOString().slice(0, 10)
                            ).slice(0, 10),
                            7
                          )
                        )
                      }
                    >
                      +7 dní
                    </button>

                    <button
                      type="button"
                      style={{ ...primaryButtonStyle, ...(saving ? disabledButtonStyle : {}) }}
                      disabled={saving}
                      onClick={saveQuickMoveDate}
                    >
                      {saving ? "Ukládám…" : "Uložit nový termín"}
                    </button>

                    {!isJednorazova ? (
                      <button
                        type="button"
                        style={{ ...secondaryButtonStyle, ...(saving ? disabledButtonStyle : {}) }}
                        disabled={saving}
                        onClick={restoreAutomaticPlan}
                      >
                        Vrátit na automatický plán
                      </button>
                    ) : null}
                  </div>

                  <div style={{ marginTop: 12, fontSize: 13, color: "#475569", lineHeight: 1.5 }}>
                    Propíše se do <strong>nejbližší plánované návštěvy</strong>
                    {isJednorazova ? "" : " i do data další návštěvy"}.
                  </div>
                </div>

                <div style={{ ...topDetailGridStyle, marginBottom: 18 }}>
                  <div
                    style={{
                      ...topDetailCardStyle,
                      borderRadius: 16,
                      boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
                    }}
                  >
                    <div style={{ ...sectionTitleStyle, marginBottom: 8 }}>Základní údaje</div>
                    <div style={topDetailMetaGridStyle}>
                      <div style={topDetailItemStyle}><div style={topDetailLabelStyle}>Objednavatel</div><div style={topDetailValueStyle}>{detail.objednavatel || "-"}</div></div>
                      <div style={topDetailItemStyle}><div style={topDetailLabelStyle}>Telefon</div><div style={topDetailValueStyle}>{detail.telefon || "-"}</div></div>
                      <div style={topDetailItemStyle}><div style={topDetailLabelStyle}>E-mail</div><div style={topDetailValueStyle}>{detail.email || "-"}</div></div>
                      <div style={topDetailItemStyle}><div style={topDetailLabelStyle}>Číslo hrobu</div><div style={topDetailValueStyle}>{detail.cisloHrobu || "-"}</div></div>
                      <div style={topDetailItemStyle}><div style={topDetailLabelStyle}>Lokace hrobu</div><div style={topDetailValueStyle}>{detail.lokaceHrobu || [detail.oddeleni, detail.skupina, detail.cisloHrobu].filter(Boolean).join(" / ") || "-"}</div></div>
                      <div style={topDetailItemStyle}><div style={topDetailLabelStyle}>Identifikace hrobu</div><div style={topDetailValueStyle}>{detail.identifikaceText || [
                        detail.majitelHrobuJmeno ? `Majitel: ${detail.majitelHrobuJmeno}` : "",
                        (detail.zesnulyPrijmeni || detail.zesnulyDatumUmrti) ? `Zesnulý: ${[detail.zesnulyPrijmeni, detail.zesnulyDatumUmrti].filter(Boolean).join(" / ")}` : "",
                        detail.jmenoNaDesce ? `Deska: ${detail.jmenoNaDesce}` : "",
                      ].filter(Boolean).join(" | ") || "-"}</div></div>
                      <div style={topDetailItemStyle}><div style={topDetailLabelStyle}>Hřbitov</div><div style={topDetailValueStyle}>{detail.hrbitov || "-"}</div></div>
                      <div style={topDetailItemStyle}><div style={topDetailLabelStyle}>Datum objednání</div><div style={topDetailValueStyle}>{formatDate(detail.datumObjednani)}</div></div>
                      <div style={topDetailItemStyle}><div style={topDetailLabelStyle}>Typ péče</div><div style={topDetailValueStyle}>{typPeceLabel(detail.typPece)}</div></div>
                      <div style={topDetailItemStyle}><div style={topDetailLabelStyle}>Opakovaná služba</div><div style={topDetailValueStyle}>{detail.opakovana ? "Ano" : "Ne"}</div></div>
                    </div>
                  </div>

                  <div
                    style={{
                      ...topDetailCardStyle,
                      borderRadius: 16,
                      boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
                    }}
                  >
                    <div style={{ ...sectionTitleStyle, marginBottom: 8 }}>Stav a návaznosti</div>

                    {nextStepHint ? (
                      <div
                        style={{
                          marginBottom: 12,
                          padding: "10px 12px",
                          borderRadius: 12,
                          border: "1px solid #fde68a",
                          background: "#fffbeb",
                          color: "#78350f",
                          lineHeight: 1.4,
                        }}
                      >
                        <div
                          style={{
                            fontSize: 11,
                            fontWeight: 800,
                            letterSpacing: 0.3,
                            textTransform: "uppercase",
                            marginBottom: 4,
                            color: "#92400e",
                          }}
                        >
                          Další krok
                        </div>
                        <div style={{ fontSize: 13, fontWeight: 700 }}>
                          {nextStepHint}
                        </div>
                      </div>
                    ) : null}

                    <div style={topDetailMetaGridStyle}>
                      <div style={topDetailPriorityItemStyle}>
                        <div style={topDetailLabelStyle}>Nejbližší plánovaná návštěva</div>
                        <div style={topDetailPriorityValueStyle}>
                          {formatDate(detail.planovaneDatum)}
                          {detail?.rucneUpravenyTermin ? (
                            <span
                              style={{
                                marginLeft: 8,
                                display: "inline-flex",
                                alignItems: "center",
                                padding: "2px 8px",
                                borderRadius: 999,
                                fontSize: 12,
                                fontWeight: 700,
                                background: "#fff7ed",
                                color: "#c2410c",
                                border: "1px solid #fdba74",
                              }}
                            >
                              Ručně upraveno
                            </span>
                          ) : null}
                        </div>
                      </div>

                      <div style={topDetailItemStyle}><div style={topDetailLabelStyle}>Datum posledního dokončení</div><div style={topDetailValueStyle}>{formatDate(detail.datumZhotoveni)}</div></div>
                      {String(detail?.typPece || "").trim() !== "jednorazova" ? (<div style={topDetailItemStyle}><div style={topDetailLabelStyle}>Počet návštěv celkem</div><div style={topDetailValueStyle}>{totalVisitsCount}</div></div>) : null}
                      {String(detail?.typPece || "").trim() !== "jednorazova" ? (<div style={topDetailItemStyle}><div style={topDetailLabelStyle}>Proběhlo návštěv</div><div style={topDetailValueStyle}>{doneVisitsCount}</div></div>) : null}
                      {String(detail?.typPece || "").trim() !== "jednorazova" ? (<div style={topDetailPriorityItemStyle}><div style={topDetailLabelStyle}>Zbývá návštěv</div><div style={topDetailPriorityValueStyle}>{remainingVisitsCount}</div></div>) : null}
                      {String(detail?.typPece || "").trim() !== "jednorazova" ? (<div style={topDetailItemStyle}><div style={topDetailLabelStyle}>Předplatné uhrazeno do</div><div style={topDetailValueStyle}>{formatDate(detail.uhrazenoDo)}</div></div>) : null}
                      {String(detail?.typPece || "").trim() !== "jednorazova" ? (<div style={topDetailItemStyle}><div style={topDetailLabelStyle}>Periodicita</div><div style={topDetailValueStyle}>{periodicitaLabel(detail.periodicita)}</div></div>) : null}
                      {String(detail?.typPece || "").trim() !== "jednorazova" ? (<div style={topDetailItemStyle}><div style={topDetailLabelStyle}>Datum od</div><div style={topDetailValueStyle}>{formatDate(detail.datumOd)}</div></div>) : null}
                      {String(detail?.typPece || "").trim() !== "jednorazova" ? (<div style={topDetailItemStyle}><div style={topDetailLabelStyle}>Datum do</div><div style={topDetailValueStyle}>{formatDate(detail.datumDo)}</div></div>) : null}
                      {String(detail?.typPece || "").trim() !== "jednorazova" ? (<div style={topDetailItemStyle}><div style={topDetailLabelStyle}>Termínů v plánu</div><div style={topDetailValueStyle}>{Array.isArray(detail.planNavstev) ? detail.planNavstev.length : 0}</div></div>) : null}

                      {String(detail?.typPece || "").trim() !== "jednorazova" ? (
                        <div style={{ ...topDetailItemStyle, gridColumn: "1 / -1" }}>
                          <div style={topDetailLabelStyle}>Zbývající plán návštěv</div>
                          <div style={topDetailValueStyle}>
                            {Array.isArray(detail.planNavstev) && detail.planNavstev.length
                              ? detail.planNavstev.map((d) => formatDate(d)).join(", ")
                              : "-"}
                          </div>
                            <div style={{ ...topDetailLabelStyle, marginTop: 10 }}>Termíny prací</div>
                            <div style={{ display: "grid", gap: 8 }}>
                              {Array.isArray(detail.terminyPraci) && detail.terminyPraci.length ? (
                                detail.terminyPraci.map((item, index) => {
                                  const line = formatTerminDetail(detail, item, index);
                                  const isFree = !!item?.jeZdarma;
                                  const isLight = String(item?.rezimNavstevy || "").trim() === "lehka_udrzba";

                                  return (
                                    <div
                                      key={`${String(item?.id || "termin")}-${index}`}
                                      style={{
                                        padding: "10px 12px",
                                        borderRadius: 10,
                                        border: "1px solid #e5e7eb",
                                        background: isFree ? "#f0fdf4" : "#fff",
                                        color: "#0f172a",
                                      }}
                                    >
                                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                                        <span style={{ fontWeight: 700 }}>{formatTerminLabel(item, index)}</span>

                                        {isFree ? (
                                          <span
                                            style={{
                                              display: "inline-flex",
                                              alignItems: "center",
                                              padding: "2px 8px",
                                              borderRadius: 999,
                                              fontSize: 12,
                                              fontWeight: 700,
                                              background: "#dcfce7",
                                              color: "#166534",
                                              border: "1px solid #86efac",
                                            }}
                                          >
                                            zdarma
                                          </span>
                                        ) : null}

                                        {isLight ? (
                                          <span
                                            style={{
                                              display: "inline-flex",
                                              alignItems: "center",
                                              padding: "2px 8px",
                                              borderRadius: 999,
                                              fontSize: 12,
                                              fontWeight: 700,
                                              background: "#e0f2fe",
                                              color: "#075985",
                                              border: "1px solid #7dd3fc",
                                            }}
                                          >
                                            lehká údržba
                                          </span>
                                        ) : null}
                                      </div>

                                      <div style={{ marginTop: 4, color: "#334155" }}>
                                        {String(item?.datum || "").trim() || "-"}
                                      </div>

                                      <div style={{ marginTop: 6, color: "#475569", fontSize: 13 }}>
                                        {line}
                                      </div>
                                    </div>
                                  );
                                })
                              ) : (
                                <div style={topDetailValueStyle}>-</div>
                              )}
                            </div>

                            {bonusRecommendedItems.length ? (
                              <>
                                <div style={{ ...topDetailLabelStyle, marginTop: 10 }}>Doporučené položky z bonusové návštěvy</div>
                                <div style={topDetailValueStyle}>
                                  {bonusRecommendedItems.join(", ")}
                                </div>
                                <div style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap" }}>
                                  <button
                                    type="button"
                                    onClick={appendBonusItemsToPoznamka}
                                    style={secondaryButtonStyle}
                                  >
                                    Převzít do poznámky
                                  </button>
                                  <button
                                    type="button"
                                    onClick={appendBonusItemsToFormItems}
                                    style={secondaryButtonStyle}
                                  >
                                    Převzít do položek
                                  </button>


                                </div>
                              </>
                            ) : null}
                        </div>
                      ) : null}
                        <div style={topDetailItemStyle}><div style={topDetailLabelStyle}>Stav předplatného</div><div style={topDetailValueStyle}><span style={getStatusBadgeStyle("predplatne", detail.stavPredplatneho)}>{subscriptionStatusLabel(detail.stavPredplatneho)}</span></div></div>
                        <div style={topDetailItemStyle}><div style={topDetailLabelStyle}>Stav obnovy</div><div style={topDetailValueStyle}><span style={renewalStatusBadgeStyle(detail?.stavObnovy || (detail?.obnovaPripravena ? "pripravena" : "-"))}>{renewalStatusLabel(detail?.stavObnovy || (detail?.obnovaPripravena ? "pripravena" : "-"))}</span></div></div>
                        <div style={topDetailItemStyle}><div style={topDetailLabelStyle}>Stav</div><div style={topDetailValueStyle}><span style={getStatusBadgeStyle("stav", detail.stav)}>{stavLabel(detail.stav || "")}</span></div></div>
                        <div style={topDetailItemStyle}><div style={topDetailLabelStyle}>Platba</div><div style={topDetailValueStyle}><span style={getStatusBadgeStyle("platba", detail.financniStav)}>{financniStavLabel(detail?.financniStav)}</span></div></div>
                        <div style={topDetailItemStyle}><div style={topDetailLabelStyle}>Kalendář</div><div style={topDetailValueStyle}>{detail?.calendarEvent?.uid ? "Zapsáno" : "Nezapsáno"}</div></div>
                        <div style={topDetailItemStyle}><div style={topDetailLabelStyle}>Datum v kalendáři</div><div style={topDetailValueStyle}>{formatDateTime(detail?.calendarEvent?.date)}</div></div>
                        <div style={topDetailItemStyle}><div style={topDetailLabelStyle}>Datum vyfakturování</div><div style={topDetailValueStyle}>{formatDateTime(detail?.datumVyfakturovani)}</div></div>
                      <div style={topDetailItemStyle}><div style={topDetailLabelStyle}>Datum obnovy</div><div style={topDetailValueStyle}>{formatDate(detail?.datumObnovy)}</div></div>
                      <div style={topDetailItemStyle}><div style={topDetailLabelStyle}>Datum potvrzení obnovy</div><div style={topDetailValueStyle}>{formatDateTime(detail?.datumPotvrzeniObnovy)}</div></div>
                      <div style={topDetailItemStyle}><div style={topDetailLabelStyle}>Poznámka k obnově</div><div style={topDetailValueStyle}>{detail?.poznamkaObnovy || "-"}</div></div>
                    </div>
                  </div>
                </div>

                <div style={detailReadCardStyle}>
                  <div style={{ ...sectionTitleStyle, marginBottom: 8 }}>Historie návštěv</div>
                  {Array.isArray(detail.navstevy) && detail.navstevy.length ? (
                    <div style={{ overflowX: "auto" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
                        <thead>
                          <tr>
                              <th style={{ textAlign: "left", padding: 10, borderBottom: "1px solid #e5e7eb" }}>Návštěva</th>
                              <th style={{ textAlign: "left", padding: 10, borderBottom: "1px solid #e5e7eb" }}>Plán</th>
                              <th style={{ textAlign: "left", padding: 10, borderBottom: "1px solid #e5e7eb" }}>Skutečně</th>
                              <th style={{ textAlign: "left", padding: 10, borderBottom: "1px solid #e5e7eb" }}>Stav po návštěvě</th>
                              <th style={{ textAlign: "left", padding: 10, borderBottom: "1px solid #e5e7eb" }}>Zbývá</th>
                              <th style={{ textAlign: "left", padding: 10, borderBottom: "1px solid #e5e7eb" }}>Poznámka</th>
                          </tr>
                        </thead>
                        <tbody>
                            {detail.navstevy.map((n, i) => (
                              <tr key={n.id || `${n.poradi || i + 1}-${n.datumSkutecne || n.datumPlan || i}`} style={visitAfterStatusRowStyle(n.stavPredplatnehoPoNavsteve)}>
                                <td style={{ ...tdStyle, fontWeight: 700 }}>Návštěva {n.poradi || i + 1}</td>
                                <td style={tdStyle}>{formatDate(n.datumPlan)}</td>
                                <td style={tdStyle}>{formatDate(n.datumSkutecne)}</td>
                                <td style={tdStyle}>
                                  <span style={visitAfterStatusBadgeStyle(n.stavPredplatnehoPoNavsteve)}>
                                    {visitAfterStatusLabel(n.stavPredplatnehoPoNavsteve || "-")}
                                  </span>
                                </td>
                                <td style={tdStyle}>{n.zbyvaNavstevPoNavsteve ?? "-"}</td>
                                <td style={tdStyle}>{n.poznamka || "-"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div style={{ color: "#6b7280" }}>Zatím bez historie návštěv.</div>
                  )}
                </div>

                <div style={{ ...detailReadCardStyle, ...softBlockStyle }}>
                  <div style={{ ...sectionTitleStyle, marginBottom: 8 }}>Historie stavů</div>

                  {Array.isArray(detail?.stavHistorie) && detail.stavHistorie.length ? (
                    <div style={{ overflowX: "auto" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
                        <thead>
                          <tr>
                            <th style={{ textAlign: "left", padding: 10, borderBottom: "1px solid #e5e7eb" }}>Kdy</th>
                            <th style={{ textAlign: "left", padding: 10, borderBottom: "1px solid #e5e7eb" }}>Typ</th>
                            <th style={{ textAlign: "left", padding: 10, borderBottom: "1px solid #e5e7eb" }}>Stav</th>
                            <th style={{ textAlign: "left", padding: 10, borderBottom: "1px solid #e5e7eb" }}>Poznámka</th>
                          </tr>
                        </thead>
                        <tbody>
                          {detail.stavHistorie.slice().reverse().map((h) => (
                            <tr key={h.id || `${h.at}-${h.status}`} style={historyStateRowStyle(h.status)}>
                              <td style={tdStyle}>{h.at || "-"}</td>
                              <td style={tdStyle}>{h.typ || "-"}</td>
                              <td style={tdStyle}><span style={historyStateBadgeStyle(h.status)}>{h.status || "-"}</span></td>
                              <td style={tdStyle}>{h.note || "-"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div style={{ color: "#6b7280" }}>Zatím bez historie stavů.</div>
                  )}
                </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                      gap: 14,
                      marginBottom: 18,
                    }}
                  >
                    <div
                      style={{
                        border: "1px solid #e5e7eb",
                        borderRadius: 16,
                        background: "#ffffff",
                        padding: "16px 18px",
                        boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
                      }}
                    >
                      <div style={{ ...sectionTitleStyle, marginBottom: 8 }}>Zadání práce</div>
                      <div style={{ whiteSpace: "pre-wrap", color: getMainServiceLabel(detail) ? "#111827" : "#6b7280" }}>
                        {getMainServiceLabel(detail) || "Bez zadání."}
                      </div>
                    </div>

                    {(String(detail?.kvetinyLimit || "").trim() || String(detail?.dekoraceLimit || "").trim()) ? (
                      <div>
                        <div style={{ fontSize: 12, color: "#64748b", marginBottom: 4 }}>Balíček – květiny a dekorace</div>
                        <div style={{ whiteSpace: "pre-wrap", color: "#111827" }}>
                          {`Květiny: ${
                            String(detail?.kvetinyLimit || "") === "vlastni"
                              ? `${Number(detail?.kvetinyVlastniCastka || detail?.kvetinyCastka || 0).toLocaleString("cs-CZ")} Kč`
                              : (Number(detail?.kvetinyCastka || 0) > 0
                                  ? `do ${Number(detail?.kvetinyCastka || 0).toLocaleString("cs-CZ")} Kč`
                                  : "bez")
                          } | Dekorace: ${
                            String(detail?.dekoraceLimit || "") === "vlastni"
                              ? `${Number(detail?.dekoraceVlastniCastka || detail?.dekoraceCastka || 0).toLocaleString("cs-CZ")} Kč`
                              : (Number(detail?.dekoraceCastka || 0) > 0
                                  ? `do ${Number(detail?.dekoraceCastka || 0).toLocaleString("cs-CZ")} Kč`
                                  : "bez")
                          }`}
                        </div>
                      </div>
                    ) : null}

                    <div
                      style={{
                        border: "1px solid #e5e7eb",
                        borderRadius: 16,
                        background: "#ffffff",
                        padding: "16px 18px",
                        boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
                      }}
                    >
                      <div style={{ ...sectionTitleStyle, marginBottom: 8 }}>Poznámka</div>
                      <div style={{ whiteSpace: "pre-wrap", color: detail.poznamka ? "#111827" : "#6b7280" }}>
                        {detail.poznamka || "Bez poznámky."}
                      </div>
                    </div>
                  </div>

              
                <div style={detailReadCardStyle}>
                  <div style={{ ...sectionTitleStyle, marginBottom: 8 }}>Služby</div>
                    {Array.isArray(activeItems) && activeItems.length ? (
                    <div style={tableWrapStyle}>
                      <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead>
                          <tr>
                            {["Název", "Množství", "Bez DPH", "DPH", "S DPH"].map((h) => (
                              <th key={h} style={thStyle}>
                                {h}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {activeItems.map((it) => (
                            <tr key={it.id}>
                              <td style={{ ...tdStyle, fontWeight: 600 }}>{it.nazev}</td>
                              <td style={tdStyle}>{it.mnozstvi}</td>
                              <td style={tdStyle}>{money(it.radekBezDph)}</td>
                              <td style={tdStyle}>{money(it.radekDph)}</td>
                              <td style={{ ...tdStyle, fontWeight: 700 }}>{money(it.radekSDph)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div style={{ color: "#6b7280" }}>Bez položek.</div>
                  )}

                  <div
                    style={{
                      marginTop: 14,
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                      gap: 12,
                    }}
                  >
                    <div style={{ ...summaryMiniCardStyle, borderRadius: 14, boxShadow: "0 1px 2px rgba(15,23,42,0.04)" }}>
                      <div style={{ fontSize: 12, color: "#64748b", marginBottom: 4 }}>Součet bez DPH</div>
                      <div style={{ fontSize: 18, fontWeight: 800 }}>{money(detail?.cenaBezDph ?? souctyReal.bezDph)}</div>
                    </div>
                    <div style={{ ...summaryMiniCardStyle, borderRadius: 14, boxShadow: "0 1px 2px rgba(15,23,42,0.04)" }}>
                      <div style={{ fontSize: 12, color: "#64748b", marginBottom: 4 }}>DPH</div>
                      <div style={{ fontSize: 18, fontWeight: 800 }}>{money(detail?.cenaDph ?? souctyReal.dph)}</div>
                    </div>
                    <div style={{ ...summaryMiniCardStyle, borderRadius: 14, boxShadow: "0 1px 2px rgba(15,23,42,0.04)" }}>
                      <div style={{ fontSize: 12, color: "#64748b", marginBottom: 4 }}>Celkem s DPH</div>
                      <div style={{ fontSize: 18, fontWeight: 800 }}>{money(detail?.cenaCelkem ?? souctyReal.sDph)}</div>
                    </div>
                  </div>
                </div>
          </>
          ) : (
            <>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
                  gap: 14,
                  marginBottom: 18,
                }}
              >
                <div
                  style={{
                    border: "1px solid #e5e7eb",
                    borderRadius: 14,
                    background: "#ffffff",
                    padding: "16px 18px",
                    boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
                  }}
                >
                  <div style={sectionTitleStyle}>Základní údaje</div>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(2, minmax(0,1fr))",
                      gap: 12,
                    }}
                  >
                    <input style={inputStyle} value={form.objednavatel || ""} onChange={(e) => updateField("objednavatel", e.target.value)} placeholder="Objednavatel" />
                    <input style={inputStyle} value={form.telefon || ""} onChange={(e) => updateField("telefon", e.target.value)} placeholder="Telefon" />
                    <input style={inputStyle} value={form.email || ""} onChange={(e) => updateField("email", e.target.value)} placeholder="E-mail" />
                    <input style={inputStyle} value={form.cisloHrobu || ""} onChange={(e) => updateField("cisloHrobu", e.target.value)} placeholder="Číslo hrobu" />
                    <input style={inputStyle} value={form.hrbitov || ""} onChange={(e) => updateField("hrbitov", e.target.value)} placeholder="Hřbitov" />

                    <div>
                      <div style={{ fontSize: 12, marginBottom: 4, color: "#475569" }}>Datum objednání</div>
                      <input style={inputStyle} type="date" value={form.datumObjednani || ""} onChange={(e) => updateField("datumObjednani", e.target.value)} />
                    </div>

                    <div>
                      <div style={{ fontSize: 12, marginBottom: 4, color: "#475569" }}>Stav zakázky</div>
                      <select style={inputStyle} value={form.stav || "nova"} onChange={(e) => updateField("stav", e.target.value)}>
                        <option value="nova">Nová</option>
                        <option value="naplanovano">Naplánováno</option>
                        <option value="rozpracovano">Rozpracováno</option>
                        <option value="dokonceno">Dokončeno</option>
                        <option value="vyfakturovano">Vyfakturováno</option>
                        <option value="zruseno">Zrušeno</option>
                        <option value="zdarma">Zdarma</option>
                        <option value="interni_test">Interní test</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    border: "1px solid #e5e7eb",
                    borderRadius: 14,
                    background: "#ffffff",
                    padding: "16px 18px",
                    boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
                  }}
                >
                  <div style={sectionTitleStyle}>Péče a návaznosti</div>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(2, minmax(0,1fr))",
                      gap: 12,
                    }}
                  >
                    <div>
                      <div style={{ fontSize: 12, marginBottom: 4, color: "#475569" }}>Nejbližší plánovaná návštěva</div>
                      <input style={inputStyle} type="date" value={form.planovaneDatum || ""} onChange={(e) => updateField("planovaneDatum", e.target.value)} />
                    </div>

                    {!isJednorazova ? (
                      <div>
                        <div style={{ fontSize: 12, marginBottom: 4, color: "#475569" }}>Datum další návštěvy</div>
                        <input style={inputStyle} type="date" value={form.dalsiNavsteva || ""} onChange={(e) => updateField("dalsiNavsteva", e.target.value)} />
                      </div>
                    ) : (
                      <div />
                    )}

                    <div>
                      <div style={{ fontSize: 12, marginBottom: 4, color: "#475569" }}>Poslední návštěva</div>
                      <input style={inputStyle} type="date" value={form.posledniNavsteva || ""} onChange={(e) => updateField("posledniNavsteva", e.target.value)} />
                    </div>

                    {isPredplatne ? (
                      <div>
                        <div style={{ fontSize: 12, marginBottom: 4, color: "#475569" }}>Předplatné uhrazeno do</div>
                        <input style={inputStyle} type="date" value={form.uhrazenoDo || ""} onChange={(e) => updateField("uhrazenoDo", e.target.value)} />
                      </div>
                    ) : (
                      <div />
                    )}

                    <select
                      style={inputStyle}
                      value={form.typPece || ""}
                      onChange={(e) => {
                        const v = e.target.value;
                        const isBal = v === "balicek";
                        const isPred = v === "predplatne";
                        const isJedno = v === "jednorazova";
                        updateField("typPece", v);
                        updateField("opakovana", isBal || isPred);
                        if (isBal) {
                          updateField("pocetNavstevCelkem", Number(form.pocetNavstevCelkem || 4));
                          updateField("zbyvaNavstev", Number(form.zbyvaNavstev || 4));
                          updateField("stavPredplatneho", "aktivni");
                        } else if (isPred) {
                          updateField("pocetNavstevCelkem", 0);
                          updateField("zbyvaNavstev", 0);
                          updateField("stavPredplatneho", "aktivni");
                        } else if (isJedno) {
                          updateField("pocetNavstevCelkem", 0);
                          updateField("zbyvaNavstev", 0);
                          updateField("stavPredplatneho", "");
                          updateField("periodicita", "");
                          updateField("dalsiNavsteva", "");
                          updateField("uhrazenoDo", "");
                          updateField("automatickyZapsatDoKalendare", false);
                        }
                      }}
                    >
                      <option value="">Typ péče</option>
                      <option value="jednorazova">Jednorázová</option>
                      <option value="predplatne">Předplatné podle času</option>
                      <option value="balicek">Balíček návštěv</option>
                    </select>

                    {isBalicek ? (
                      <input style={inputStyle} type="number" min="0" value={Array.isArray(form.planNavstev) ? form.planNavstev.length : Number(form.pocetNavstevCelkem || 0)} readOnly placeholder="Počet návštěv v balíčku" />
                    ) : (
                      <div />
                    )}

                    {isBalicek ? (
                      <input style={inputStyle} type="number" min="0" value={Array.isArray(form.planNavstev) ? form.planNavstev.length : Number(form.zbyvaNavstev || 0)} readOnly placeholder="Zbývá návštěv z balíčku" />
                    ) : (
                      <div />
                    )}

                    {!isJednorazova ? (
                      <select style={inputStyle} value={form.stavPredplatneho || "aktivni"} onChange={(e) => updateField("stavPredplatneho", e.target.value)}>
                        <option value="aktivni">Aktivní</option>
                        <option value="konci_brzy">Končí brzy</option>
                        <option value="ceka_na_obnovu">Čeká na obnovu</option>
                        <option value="vyprselo">Vypršelo</option>
                      </select>
                    ) : (
                      <div />
                    )}

                    {!isJednorazova ? (
                      <select style={inputStyle} value={form.periodicita || ""} onChange={(e) => updateField("periodicita", e.target.value)}>
                        <option value="">Periodicita</option>
                        <option value="mesicne">Měsíčně</option>
                        <option value="ctvrtletne">Čtvrtletně</option>
                        <option value="pulrocne">Půlročně</option>
                        <option value="rocne">Ročně</option>
                        <option value="dle_domluvy">Dle domluvy</option>
                      </select>
                    ) : (
                      <div />
                    )}

                    {!isJednorazova ? (
                      <label style={checkboxRowStyle}>
                        <input type="checkbox" checked={!!form.opakovana} readOnly />
                        Opakovaná služba
                      </label>
                    ) : (
                      <div />
                    )}

                    <label style={checkboxRowStyle}>
                      <input
                        type="checkbox"
                        checked={!!form.automatickyZapsatDoKalendare}
                        onChange={(e) => updateField("automatickyZapsatDoKalendare", e.target.checked)}
                      />
                      Automaticky zapisovat do kalendáře
                    </label>

                    <input style={inputStyle} value={form.poznamkaObnovy || ""} onChange={(e) => updateField("poznamkaObnovy", e.target.value)} placeholder="Poznámka k obnově" />
                  </div>
                </div>
              </div>

              <div style={sectionCardStyle}>
                <div style={{ ...sectionTitleStyle, marginBottom: 8 }}>Zadání práce</div>
                <textarea
                  style={{ ...inputStyle, minHeight: 96, resize: "vertical" }}
                  value={form.zadaniPrace || ""}
                  onChange={(e) => updateField("zadaniPrace", e.target.value)}
                />
              </div>

              <div style={sectionCardStyle}>
                <div style={{ ...sectionTitleStyle, marginBottom: 8 }}>Poznámka</div>
                <textarea
                  style={{ ...inputStyle, minHeight: 84, resize: "vertical" }}
                  value={form.poznamka || ""}
                  onChange={(e) => updateField("poznamka", e.target.value)}
                />
              </div>

              <div style={{ ...sectionCardStyle, ...softBlockStyle }}>
                <div style={sectionTitleStyle}>Přidat službu z ceníku</div>
                <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr auto", gap: 10 }}>
                  <select style={inputStyle} value={selectedCenikId} onChange={(e) => setSelectedCenikId(e.target.value)}>
                    {cenik.map((it) => (
                      <option key={it.id} value={it.id}>
                        {it.nazev}
                      </option>
                    ))}
                  </select>

                  <input
                    style={inputStyle}
                    value={customName}
                    onChange={(e) => setCustomName(e.target.value)}
                    placeholder="Název pro Jiné"
                  />

                  <input
                    style={inputStyle}
                    type="number"
                    min="0"
                    value={customPrice}
                    onChange={(e) => setCustomPrice(Number(e.target.value || 0))}
                    placeholder="Cena"
                  />

                  <button type="button" style={primaryButtonStyle} onClick={addItemFromCenik}>Přidat</button>
                </div>
              </div>

              <div style={detailReadCardStyle}>
                <div style={{ ...sectionTitleStyle, marginBottom: 8 }}>Služby</div>
                {!form.items?.length ? <div style={{ color: "#6b7280" }}>Bez položek.</div> : null}

                {(form.items || []).map((it) => {
                  const row = calcRow(it);

    return (
                    <div
                      key={it.id}
                      style={{ ...sectionCardStyle, marginBottom: 10, padding: 12 }}
                    >
                      <div style={{ ...sectionTitleStyle, marginBottom: 8 }}>{it.nazev}</div>

                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 130px", gap: 12 }}>
                        <div>
                          <div style={{ fontSize: 12, marginBottom: 4 }}>Množství</div>
                          <input
                            style={inputStyle}
                            type="number"
                            min="1"
                            value={it.mnozstvi}
                            onChange={(e) => updateItem(it.id, { mnozstvi: Number(e.target.value || 1) })}
                          />
                        </div>

                        <div>
                          <div style={{ fontSize: 12, marginBottom: 4 }}>Cena bez DPH</div>
                          <input
                            style={inputStyle}
                            type="number"
                            min="0"
                            value={it.cenaBezDph}
                            onChange={(e) => updateItem(it.id, { cenaBezDph: Number(e.target.value || 0) })}
                          />
                        </div>

                        <div style={{ display: "flex", alignItems: "end" }}>
                          <button type="button" onClick={() => removeItem(it.id)} style={{ ...primaryButtonStyle, width: "100%" }}>
                            Smazat
                          </button>
                        </div>
                      </div>

                      <div style={{ marginTop: 10, fontSize: 13, color: "#475569", paddingTop: 10, borderTop: "1px dashed #e2e8f0" }}>
                        Bez DPH: <strong>{money(row.bez)}</strong> • DPH: <strong>{money(row.dph)}</strong> • S DPH: <strong>{money(row.sdph)}</strong>
                      </div>
                    </div>
                  );
                })}

                <div style={{ marginTop: 12, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12 }}>
                  <div style={summaryMiniCardStyle}>
                    <div style={{ fontSize: 12, color: "#64748b", marginBottom: 4 }}>Součet bez DPH</div>
                    <div style={{ fontSize: 18, fontWeight: 800 }}>{money(souctyReal.bezDph)}</div>
                  </div>
                  <div style={summaryMiniCardStyle}>
                    <div style={{ fontSize: 12, color: "#64748b", marginBottom: 4 }}>DPH</div>
                    <div style={{ fontSize: 18, fontWeight: 800 }}>{money(souctyReal.dph)}</div>
                  </div>
                  <div style={summaryMiniCardStyle}>
                    <div style={{ fontSize: 12, color: "#64748b", marginBottom: 4 }}>Celkem s DPH</div>
                    <div style={{ fontSize: 18, fontWeight: 800 }}>{money(souctyReal.sDph)}</div>
                  </div>
                </div>
              </div>
            </>
        )}

        {!editMode ? (
            <div style={{ ...sectionCardStyle, ...softBlockStyle }}>
              <div style={sectionTitleStyle}>Finance zakázky</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12 }}>
                <div style={summaryMiniCardStyle}>
                  <div style={{ fontSize: 12, color: "#64748b", marginBottom: 4 }}>Cena celkem</div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: "#111827", lineHeight: 1.2 }}>{money(detail?.cenaCelkem ?? cenaCelkemNum)}</div>
                </div>
                <div style={summaryMiniCardStyle}>
                  <div style={{ fontSize: 12, color: "#64748b", marginBottom: 4 }}>Záloha</div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: "#111827", lineHeight: 1.2 }}>{money(detail?.zalohaCastka)}</div>
                </div>
                <div style={summaryMiniCardStyle}>
                  <div style={{ fontSize: 12, color: "#64748b", marginBottom: 4 }}>Uhrazeno celkem</div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: "#111827", lineHeight: 1.2 }}>{money(detail?.uhrazenoCelkem)}</div>
                </div>
                <div style={summaryMiniCardStyle}>
                  <div style={{ fontSize: 12, color: "#64748b", marginBottom: 4 }}>Doplatek</div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: "#991b1b", lineHeight: 1.2 }}>{money(detail?.doplatekCastka)}</div>
                </div>
              </div>
              <div style={{ marginTop: 12 }}>
                <span style={{ fontSize: 13, color: "#475569", marginRight: 8, fontWeight: 600 }}>Finanční stav</span>
                <span style={financniStavBadgeStyle(detail?.financniStav)}>{financniStavLabel(detail?.financniStav)}</span>
              </div>
            </div>
          ) : (
            <div style={{ ...sectionCardStyle, ...softBlockStyle, padding: 12 }}>
              <div style={{ ...sectionTitleStyle, marginBottom: 8 }}>Finance zakázky</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0,1fr))", gap: 12 }}>
                <div>
                  <div style={{ fontSize: 12, marginBottom: 4, color: "#475569" }}>Cena celkem (Kč)</div>
                  <input
                    style={{ ...inputStyle, background: "#f8fafc", color: "#0f172a" }}
                    type="number"
                    min="0"
                    step="0.01"
                    value={cenaCelkemNum}
                    readOnly
                  />
                </div>

                <div>
                  <div style={{ fontSize: 12, marginBottom: 4, color: "#475569" }}>Záloha (Kč)</div>
                  <input
                    style={inputStyle}
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.zalohaCastka ?? 0}
                    onChange={(e) => updateField("zalohaCastka", e.target.value)}
                  />
                </div>

                <div>
                  <div style={{ fontSize: 12, marginBottom: 4, color: "#475569" }}>Uhrazeno celkem (Kč)</div>
                  <input
                    style={inputStyle}
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.uhrazenoCelkem ?? 0}
                    onChange={(e) => updateField("uhrazenoCelkem", e.target.value)}
                  />
                </div>

                <div>
                  <div style={{ fontSize: 12, marginBottom: 4, color: "#475569" }}>Zbývá doplatit (Kč)</div>
                  <input
                    style={{ ...inputStyle, background: "#f8fafc", color: "#0f172a" }}
                    type="number"
                    value={doplatekCastkaNum}
                    readOnly
                  />
                </div>
              </div>

              <div style={{ marginTop: 12, display: "flex", gap: 12, flexWrap: "wrap" }}>
                <span style={chipBaseStyle}>
                  Finanční stav: <strong>{financniStavAuto}</strong>
                </span>
                <span style={chipBaseStyle}>
                  Cena: <strong>{cenaCelkemNum.toLocaleString("cs-CZ")} Kč</strong>
                </span>
                <span style={chipBaseStyle}>
                  Uhrazeno: <strong>{uhrazenoCelkemNum.toLocaleString("cs-CZ")} Kč</strong>
                </span>
                <span style={chipBaseStyle}>
                  Doplatek: <strong>{doplatekCastkaNum.toLocaleString("cs-CZ")} Kč</strong>
                </span>
              </div>
            </div>
          )}

          <div style={{ marginTop: 18, marginBottom: 18, border: "1px solid #e5e7eb", borderRadius: 14, padding: 14, background: "#f9fafb" }}>
          <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 10 }}>Interní nákup zboží</div>
          <div style={{ color: "#6b7280", marginBottom: 12 }}>
            Tato sekce je pouze interní. Jen položky označené „vložit do faktury“ se po uložení propíšou do služeb zakázky s marží {interniSouctyPreview.cilovaMarzeProc} %.
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(180px, 1fr))", gap: 10, marginBottom: 14 }}>
            <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: 12 }}>
              <div style={{ fontSize: 12, color: "#6b7280" }}>Nákupy celkem s DPH</div>
              <div style={{ fontSize: 20, fontWeight: 700 }}>{money(interniSouctyPreview.nakupCelkemSDph)}</div>
            </div>
            <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: 12 }}>
              <div style={{ fontSize: 12, color: "#6b7280" }}>Označeno do faktury</div>
              <div style={{ fontSize: 20, fontWeight: 700 }}>{interniSouctyPreview.kFakturaciPocet}</div>
            </div>
            <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: 12 }}>
              <div style={{ fontSize: 12, color: "#6b7280" }}>Nákup do faktury s DPH</div>
              <div style={{ fontSize: 20, fontWeight: 700 }}>{money(interniSouctyPreview.kFakturaciNakupSDph)}</div>
            </div>
            <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: 12 }}>
              <div style={{ fontSize: 12, color: "#6b7280" }}>Prodej do faktury s marží</div>
              <div style={{ fontSize: 20, fontWeight: 700 }}>{money(interniSouctyPreview.doporucenaProdejniCenaKfakturaciSDph)}</div>
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "2fr 1.2fr 1fr 0.8fr 1fr 1.5fr auto",
              gap: 8,
              marginBottom: 10,
            }}
          >
            <input style={inputStyle} placeholder="Název zboží" value={nakupDraft.nazev} onChange={(e) => setNakupDraft((p) => ({ ...p, nazev: e.target.value }))} />
            <input style={inputStyle} placeholder="Dodavatel" value={nakupDraft.dodavatel} onChange={(e) => setNakupDraft((p) => ({ ...p, dodavatel: e.target.value }))} />
            <input style={inputStyle} type="date" value={nakupDraft.datum} onChange={(e) => setNakupDraft((p) => ({ ...p, datum: e.target.value }))} />
            <input style={inputStyle} type="number" min="1" step="1" value={nakupDraft.mnozstvi} onChange={(e) => setNakupDraft((p) => ({ ...p, mnozstvi: Number(e.target.value || 1) }))} />
            <input style={inputStyle} type="number" min="0" step="0.01" placeholder="Cena/ks s DPH" value={nakupDraft.cenaZaKsSDph} onChange={(e) => setNakupDraft((p) => ({ ...p, cenaZaKsSDph: e.target.value }))} />
            <input style={inputStyle} placeholder="Poznámka" value={nakupDraft.poznamka} onChange={(e) => setNakupDraft((p) => ({ ...p, poznamka: e.target.value }))} />
            <button type="button" style={primaryButtonStyle} onClick={addNakupRow}>Přidat</button>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 2fr",
              gap: 12,
              alignItems: "center",
              marginBottom: 12,
            }}
          >
            <label style={{ ...checkboxRowStyle, fontWeight: 600 }}>
              <input
                type="checkbox"
                checked={!!nakupDraft.zahrnoutDoFaktury}
                onChange={(e) => setNakupDraft((p) => ({ ...p, zahrnoutDoFaktury: e.target.checked }))}
              />
              Vložit do faktury
            </label>

            <input
              style={inputStyle}
              placeholder="Popis do faktury, např. Kamínky – dosypání hrobu"
              value={nakupDraft.popisDoFaktury}
              onChange={(e) => setNakupDraft((p) => ({ ...p, popisDoFaktury: e.target.value }))}
            />
          </div>

          {(form.nakupy || []).length ? (
            <div style={tableWrapStyle}>
              <table style={{ width: "100%", borderCollapse: "collapse", background: "#fff" }}>
                <thead>
                  <tr style={{ background: "#f8fafc" }}>
                    <th style={{ ...thStyle, border: "1px solid #e5e7eb" }}>Název</th>
                    <th style={{ ...thStyle, border: "1px solid #e5e7eb" }}>Dodavatel</th>
                    <th style={{ ...thStyle, border: "1px solid #e5e7eb" }}>Datum</th>
                    <th style={{ ...thStyle, textAlign: "right", border: "1px solid #e5e7eb" }}>Množství</th>
                    <th style={{ ...thStyle, textAlign: "right", border: "1px solid #e5e7eb" }}>Cena/ks s DPH</th>
                    <th style={{ ...thStyle, textAlign: "right", border: "1px solid #e5e7eb" }}>Celkem s DPH</th>
                    <th style={{ textAlign: "center", padding: 8, border: "1px solid #e5e7eb", color: "#64748b", fontSize: 12 }}>Do faktury</th>
                     <th style={{ textAlign: "left", padding: 8, border: "1px solid #e5e7eb", color: "#64748b", fontSize: 12 }}>Popis do faktury</th>
                     <th style={{ textAlign: "left", padding: 8, border: "1px solid #e5e7eb", color: "#64748b", fontSize: 12 }}>Poznámka</th>
                     <th style={{ textAlign: "center", padding: 8, border: "1px solid #e5e7eb", color: "#64748b", fontSize: 12 }}>Přepnout</th>
                     <th style={{ textAlign: "center", padding: 8, border: "1px solid #e5e7eb", color: "#64748b", fontSize: 12 }}>Akce</th>
                  </tr>
                </thead>
                <tbody>
                  {(form.nakupy || []).map((it) => {
                    const row = calcNakupRow(it);
                    return (
                      <tr key={it.id}>
                        <td style={{ padding: 8, border: "1px solid #e5e7eb" }}>{it.nazev || "-"}</td>
                        <td style={{ padding: 8, border: "1px solid #e5e7eb" }}>{it.dodavatel || "-"}</td>
                        <td style={{ padding: 8, border: "1px solid #e5e7eb" }}>{it.datum || "-"}</td>
                        <td style={{ padding: 8, border: "1px solid #e5e7eb", textAlign: "right" }}>{Number(it.mnozstvi || 0)}</td>
                        <td style={{ padding: 8, border: "1px solid #e5e7eb", textAlign: "right" }}>{money(it.cenaZaKsSDph || 0)}</td>
                        <td style={{ padding: 8, border: "1px solid #e5e7eb", textAlign: "right", fontWeight: 700 }}>{money(row.celkemSDph)}</td>
                        <td style={{ padding: 8, border: "1px solid #e5e7eb", textAlign: "center", fontWeight: 700 }}>
                          {it.zahrnoutDoFaktury ? "Ano" : "Ne"}
                        </td>
                        <td style={{ padding: 8, border: "1px solid #e5e7eb" }}>
                          <input
                            style={{ ...inputStyle, minWidth: 220 }}
                            placeholder="Popis do faktury"
                            value={it.popisDoFaktury || ""}
                            onChange={(e) => updateNakupPopis(it.id, e.target.value)}
                          />
                        </td>
                        <td style={{ padding: 8, border: "1px solid #e5e7eb" }}>{it.poznamka || ""}</td>
                        <td style={{ padding: 8, border: "1px solid #e5e7eb", textAlign: "center" }}>
                          <button type="button" style={primaryButtonStyle} onClick={() => toggleNakupDoFaktury(it.id)}>
                            {it.zahrnoutDoFaktury ? "Vypnout" : "Zapnout"}
                          </button>
                        </td>
                        <td style={{ padding: 8, border: "1px solid #e5e7eb", textAlign: "center" }}>
                          <button type="button" style={primaryButtonStyle} onClick={() => removeNakupRow(it.id)}>Smazat</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ color: "#6b7280", marginBottom: 10 }}>Zatím bez interních nákupů.</div>
          )}

          <div style={{ marginTop: 12, display: "grid", gridTemplateColumns: "repeat(2, minmax(220px, 1fr))", gap: 8 }}>
            <div><strong>Nákup celkem s DPH:</strong> {money(interniSouctyPreview.nakupCelkemSDph)}</div>
            <div><strong>Doporučený prodej při 50 % marži:</strong> {money(interniSouctyPreview.doporucenaProdejniCenaSDph)}</div>
            <div><strong>Skutečný prodej:</strong> {money(interniSouctyPreview.skutecnyProdejSDph)}</div>
            <div><strong>Skutečná marže v Kč:</strong> {money(interniSouctyPreview.skutecnaMarzeSDph)}</div>
            <div><strong>Skutečná marže v % z nákupu:</strong> {interniSouctyPreview.skutecnaMarzeProcZNakupu.toLocaleString("cs-CZ")} %</div>
          </div>

          <div style={{ marginTop: 16, borderTop: "1px solid #e5e7eb", paddingTop: 12 }}>
            <div style={{ ...sectionTitleStyle, marginBottom: 8 }}>Náhled položek, které půjdou do faktury</div>
            {previewFakturace.length ? (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", background: "#fff" }}>
                  <thead>
                    <tr style={{ background: "#f3f4f6" }}>
                      <th style={{ textAlign: "left", padding: 8, border: "1px solid #e5e7eb" }}>Název</th>
                      <th style={{ textAlign: "right", padding: 8, border: "1px solid #e5e7eb" }}>Množství</th>
                      <th style={{ textAlign: "right", padding: 8, border: "1px solid #e5e7eb" }}>Bez DPH</th>
                      <th style={{ textAlign: "right", padding: 8, border: "1px solid #e5e7eb" }}>DPH</th>
                      <th style={{ textAlign: "right", padding: 8, border: "1px solid #e5e7eb" }}>S DPH</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewFakturace.map((it) => (
                      <tr key={it.id}>
                        <td style={{ padding: 8, border: "1px solid #e5e7eb" }}>{it.nazev}</td>
                        <td style={{ padding: 8, border: "1px solid #e5e7eb", textAlign: "right" }}>{it.mnozstvi}</td>
                        <td style={{ padding: 8, border: "1px solid #e5e7eb", textAlign: "right" }}>{money(it.bez)}</td>
                        <td style={{ padding: 8, border: "1px solid #e5e7eb", textAlign: "right" }}>{money(it.dph)}</td>
                        <td style={{ padding: 8, border: "1px solid #e5e7eb", textAlign: "right", fontWeight: 700 }}>{money(it.sdph)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={{ color: "#6b7280" }}>Zatím není označena žádná interní položka do faktury.</div>
            )}
          </div>

          <div style={{ marginTop: 12 }}>
            <button type="button" style={primaryButtonStyle} onClick={() => saveNakupyOnly(form.nakupy || [])}>Uložit interní náklady</button>
          </div>
        </div>

        <Gallery
          title="Fotodokumentace před"
          items={detail.fotoPred || []}
          kind="fotoPred"
          onPick={onPickPred}
          inputRef={predRef}
          uploading={uploadingPred}
          onRemove={(url) => removePhoto("fotoPred", url)}
        />

        <Gallery
          title="Fotodokumentace po"
          items={detail.fotoPo || []}
          kind="fotoPo"
          onPick={onPickPo}
          inputRef={poRef}
          uploading={uploadingPo}
          onRemove={(url) => removePhoto("fotoPo", url)}
        />
      </div>
    </div>
  );
}
